<template>
  <div class="bg-white shadow-sm border border-slate-200 overflow-hidden">
    <div v-if="pageLoading" class="px-6 py-12 flex justify-center items-center">
      <div class="h-8 w-8 border-2 border-[#0d4169]/30 border-t-transparent rounded-full animate-spin"></div>
    </div>

    <template v-else>
    <div class="px-6 pt-2 pb-1">
      <div class="flex items-baseline gap-2 flex-wrap">
        <h2 class="text-base font-bold text-[#0d4169] text-left">
          {{ resolvedCompanyName }}
        </h2>
        <span class="text-sm font-bold text-slate-600">Cash and Credit Income</span>
      </div>
    </div>

    <form @submit.prevent="save">
      <div class="px-6 pb-3 pt-2">
        <div class="flex items-center justify-between gap-6 flex-wrap w-full">
          <div class="flex items-center gap-3 min-w-[220px]">
            <label class="text-[0.78rem] font-semibold text-[#1e293b] shrink-0">Select Date:</label>
            <input
              v-model="calendarDate"
              type="date"
              class="flex-1 min-w-[9rem] px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer"
              @change="onCalendarChange"
            >
          </div>

          <div v-if="activeTab === 'credit'" class="flex items-center gap-3 min-w-[220px]">
            <label class="text-[0.78rem] font-semibold text-[#1e293b] shrink-0">Select Company:</label>
            <select
              v-model="selectedDeliveryCompanyId"
              class="flex-1 min-w-[9rem] px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer hover:bg-slate-50"
              @change="onDeliveryCompanyChange"
            >
              <option value="">Select Company</option>
              <option v-for="dc in deliveryCompanies" :key="dc.id" :value="String(dc.id)">{{ dc.name }}</option>
            </select>
          </div>

          <div class="flex items-center gap-6 shrink-0">
            <button
              type="button"
              @click="switchTab('cash')"
              :class="activeTab === 'cash' ? 'border-[#0d4169] text-[#0d4169]' : 'border-transparent text-slate-500'"
              class="pb-1.5 text-sm font-bold border-b-2 hover:text-[#0d4169] transition-colors tracking-wide"
            >
              Cash
            </button>
            <button
              type="button"
              @click="switchTab('credit')"
              :class="[
                'pb-1.5 text-sm font-bold border-b-2 transition-colors tracking-wide',
                activeTab === 'credit'
                  ? 'border-red-600 text-red-600 bg-red-50'
                  : 'border-transparent text-red-600 hover:bg-red-50/60'
              ]"
            >
              Credit
            </button>
          </div>
        </div>
      </div>

      <div class="px-6 pt-2 pb-6">
        <div
          :class="[
            'text-[0.78rem] font-semibold capitalize tracking-wide text-center',
            activeTab === 'cash'
              ? 'bg-[#0d4169] text-white rounded-t-md py-1.5'
              : 'text-[#1e293b] pb-2'
          ]"
        >
          {{ activeTab }} Income
        </div>
        <div class="relative overflow-auto custom-scrollbar border border-slate-200 rounded-lg shadow-sm bg-white">
          <table class="w-full today-cash-table min-w-max">
            <thead>
              <tr>
                <th class="col-sn">S/N</th>
                <th class="col-income">Income</th>
                <th class="num col-sys-hidden">Sales<br>(Sys) Taxable</th>
                <th class="num">Sales<br>(Man) Taxable</th>
                <th class="num total-col">Total<br>Taxable</th>
                <th class="num col-sys-hidden">Sales<br>(Sys) Non-Taxable</th>
                <th class="num">Sales<br>(Man) Non-Taxable</th>
                <th class="num total-col">Total<br>Non-Taxable</th>
                <th class="num col-sys-hidden">Sales (Sys)<br>Taxable SC</th>
                <th class="num">Sales (Man)<br>Taxable SC</th>
                <th class="num total-col">Total<br>Taxable SC</th>
                <th class="num col-sys-hidden">Sales (Sys)<br>Non-Taxable SC</th>
                <th class="num">Sales (Man)<br>Non-Taxable SC</th>
                <th class="num total-col">Total<br>Non-Taxable SC</th>
                <th class="num">Total<br>Before</th>
                <th class="num col-sys-hidden">VAT<br>(Sys)</th>
                <th class="num">VAT<br>(Man)</th>
                <th class="num">Variation</th>
                <th class="num total-col">Total<br>VAT</th>
                <th class="num">Total<br>Sales</th>
                <th class="col-footer-actions">{{ showBankDepositColumn ? 'Bank Deposit' : 'Remark' }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, idx) in rows"
                :key="row.incomeCategoryId"
                class="hover:bg-slate-50 transition-colors border-b"
              >
                <td class="col-sn">{{ idx + 1 }}</td>
                <td
                  class="col-income"
                  :class="{ 'font-bold text-slate-700': isIncomeCategoryRow(row) }"
                >{{ row.categoryName }}</td>
                <td class="num col-sys-hidden">
                  <template v-if="isDepartmentRow(row)">
                    <span class="font-semibold text-slate-600">{{ fmt(departmentSales(row.incomeCategoryId).taxable) }}</span>
                  </template>
                  <!-- <input
                    v-else
                    v-model.number="row.salesSysTaxable"
                    type="number"
                    step="0.01"
                    min="0"
                    class="income-table-field"
                  > -->
                </td>
                <td class="num">
                  <input
                    v-model.number="row.salesManTaxable"
                    type="number"
                    step="0.01"
                    min="0"
                    class="income-table-field"
                    :readonly="cashManualFieldsReadonly"
                    @input="applyManDerivedFields(row)"
                  >
                </td>
                <td class="num total-cell">
                  <div
                    class="total-cell-wrap pinned-breakdown-wrap"
                    @mouseenter="pinBreakdownDropdown"
                    @mouseleave="resetBreakdownDropdown"
                  >
                    <span class="total-cell-value">{{ fmt(rowSalesTotal(row, 'taxable')) }}</span>
                    <div class="total-cell-dropdown">
                      <div class="total-cell-dropdown-title">Breakdown</div>
                      <div class="total-cell-dropdown-line"><span class="total-cell-tag">System</span> Sales (Sys) Taxable: <strong>{{ fmt(rowSalesSys(row, 'taxable')) }}</strong></div>
                      <div class="total-cell-dropdown-line"><span class="total-cell-tag">Manual</span> Sales (Man) Taxable: <strong>{{ fmt(rowSalesMan(row, 'taxable')) }}</strong></div>
                    </div>
                  </div>
                </td>
                <td class="num col-sys-hidden">
                  <template v-if="isDepartmentRow(row)">
                    <span class="font-semibold text-slate-600">{{ fmt(departmentSales(row.incomeCategoryId).nonTaxable) }}</span>
                  </template>
                  <!-- <input
                    v-else
                    v-model.number="row.salesSysNoTax"
                    type="number"
                    step="0.01"
                    min="0"
                    class="income-table-field"
                  > -->
                </td>
                <td class="num">
                  <input
                    v-model.number="row.salesManNoTax"
                    type="number"
                    step="0.01"
                    min="0"
                    class="income-table-field"
                    :readonly="cashManualFieldsReadonly"
                    @input="applyManDerivedFields(row)"
                  >
                </td>
                <td class="num total-cell">
                  <div
                    class="total-cell-wrap pinned-breakdown-wrap"
                    @mouseenter="pinBreakdownDropdown"
                    @mouseleave="resetBreakdownDropdown"
                  >
                    <span class="total-cell-value">{{ fmt(rowSalesTotal(row, 'nonTaxable')) }}</span>
                    <div class="total-cell-dropdown">
                      <div class="total-cell-dropdown-title">Breakdown</div>
                      <div class="total-cell-dropdown-line"><span class="total-cell-tag">System</span> Sales (Sys) Non-Taxable: <strong>{{ fmt(rowSalesSys(row, 'nonTaxable')) }}</strong></div>
                      <div class="total-cell-dropdown-line"><span class="total-cell-tag">Manual</span> Sales (Man) Non-Taxable: <strong>{{ fmt(rowSalesMan(row, 'nonTaxable')) }}</strong></div>
                    </div>
                  </div>
                </td>
                <td class="num col-sys-hidden">
                  <template v-if="isDepartmentRow(row)">
                    <span class="font-semibold text-slate-600">{{ fmt(departmentSales(row.incomeCategoryId).taxableSc) }}</span>
                  </template>
                  <!-- <input
                    v-else
                    v-model.number="row.salesSysTaxableSc"
                    type="number"
                    step="0.01"
                    min="0"
                    class="income-table-field"
                  > -->
                </td>
                <td class="num">
                  <input
                    v-model.number="row.salesManTaxableSc"
                    type="number"
                    step="0.01"
                    min="0"
                    class="income-table-field"
                    :readonly="cashManualFieldsReadonly"
                  >
                </td>
                <td class="num total-cell">
                  <div
                    class="total-cell-wrap pinned-breakdown-wrap"
                    @mouseenter="pinBreakdownDropdown"
                    @mouseleave="resetBreakdownDropdown"
                  >
                    <span class="total-cell-value">{{ fmt(rowSalesTotal(row, 'taxableSc')) }}</span>
                    <div class="total-cell-dropdown">
                      <div class="total-cell-dropdown-title">Breakdown</div>
                      <div class="total-cell-dropdown-line"><span class="total-cell-tag">System</span> Sales (Sys) Taxable SC: <strong>{{ fmt(rowSalesSys(row, 'taxableSc')) }}</strong></div>
                      <div class="total-cell-dropdown-line"><span class="total-cell-tag">Manual</span> Sales (Man) Taxable SC: <strong>{{ fmt(rowSalesMan(row, 'taxableSc')) }}</strong></div>
                    </div>
                  </div>
                </td>
                <td class="num col-sys-hidden">
                  <template v-if="isDepartmentRow(row)">
                    <span class="font-semibold text-slate-600">{{ fmt(departmentSales(row.incomeCategoryId).nonTaxableSc) }}</span>
                  </template>
                  <!-- <input
                    v-else
                    v-model.number="row.salesSysNoTaxSc"
                    type="number"
                    step="0.01"
                    min="0"
                    class="income-table-field"
                  > -->
                </td>
                <td class="num">
                  <input
                    v-model.number="row.salesManNoTaxSc"
                    type="number"
                    step="0.01"
                    min="0"
                    class="income-table-field"
                    :readonly="cashManualFieldsReadonly"
                  >
                </td>
                <td class="num total-cell">
                  <div
                    class="total-cell-wrap pinned-breakdown-wrap"
                    @mouseenter="pinBreakdownDropdown"
                    @mouseleave="resetBreakdownDropdown"
                  >
                    <span class="total-cell-value">{{ fmt(rowSalesTotal(row, 'nonTaxableSc')) }}</span>
                    <div class="total-cell-dropdown">
                      <div class="total-cell-dropdown-title">Breakdown</div>
                      <div class="total-cell-dropdown-line"><span class="total-cell-tag">System</span> Sales (Sys) Non-Taxable SC: <strong>{{ fmt(rowSalesSys(row, 'nonTaxableSc')) }}</strong></div>
                      <div class="total-cell-dropdown-line"><span class="total-cell-tag">Manual</span> Sales (Man) Non-Taxable SC: <strong>{{ fmt(rowSalesMan(row, 'nonTaxableSc')) }}</strong></div>
                    </div>
                  </div>
                </td>
                <td class="num">{{ fmt(rowTotalBefore(row)) }}</td>
                <td class="num col-sys-hidden">{{ fmt(rowVatSys(row)) }}</td>
                <td class="num">
                  <input
                    v-model.number="row.vatMan"
                    type="number"
                    step="0.01"
                    min="0"
                    class="income-table-field"
                    :readonly="cashManualFieldsReadonly"
                  >
                </td>
                <td class="num variation-cell">{{ fmt(rowVatManVariation(row)) }}</td>
                <td class="num total-cell">
                  <div
                    class="total-cell-wrap pinned-breakdown-wrap"
                    @mouseenter="pinBreakdownDropdown"
                    @mouseleave="resetBreakdownDropdown"
                  >
                    <span class="total-cell-value vat-total-value">{{ fmt(rowVatTotal(row)) }}</span>
                    <div class="total-cell-dropdown">
                      <div class="total-cell-dropdown-title">Breakdown</div>
                      <div class="total-cell-dropdown-line"><span class="total-cell-tag">System</span> VAT (Sys): <strong>{{ fmt(rowVatSys(row)) }}</strong></div>
                      <div class="total-cell-dropdown-line"><span class="total-cell-tag">Manual</span> VAT (Man): <strong>{{ fmt(rowVatMan(row)) }}</strong></div>
                    </div>
                  </div>
                </td>
                <td class="num font-bold text-slate-700">{{ fmt(rowTotalSales(row)) }}</td>
                <td class="col-footer-actions"></td>
              </tr>
              <tr v-if="rows.length === 0">
                <td :colspan="formTableColspan" style="text-align:center; color:#94a3b8; padding: 32px 15px; font-style: italic;">
                  No enabled income categories. Register income categories first.
                </td>
              </tr>
            </tbody>
            <tfoot v-if="rows.length > 0">
              <tr class="h-3 border-none bg-transparent">
                <td :colspan="formTableColspan" class="border-none p-0"></td>
              </tr>
              <tr class="footer-total font-semibold bg-slate-100/80 border-t border-slate-200">
                <td colspan="2">
                  <button
                    type="button"
                    class="footer-link-btn"
                    @click="openIncomeModalByName('Total Sales')"
                  >
                    Total Sales
                  </button>
                </td>
                <td class="num col-sys-hidden">{{ fmt(sumCol('salesSysTaxable')) }}</td>
                <td class="num">{{ fmt(sumCol('salesManTaxable')) }}</td>
                <td class="num total-cell">
                  <div
                    class="total-cell-wrap pinned-breakdown-wrap"
                    @mouseenter="pinBreakdownDropdown"
                    @mouseleave="resetBreakdownDropdown"
                  >
                    <span class="total-cell-value">{{ fmt(sumTotalPair('salesSysTaxable', 'salesManTaxable')) }}</span>
                    <div class="total-cell-dropdown">
                      <div class="total-cell-dropdown-title">Breakdown</div>
                      <div class="total-cell-dropdown-line"><span class="total-cell-tag">System</span> Sales (Sys) Taxable: <strong>{{ fmt(sumCol('salesSysTaxable')) }}</strong></div>
                      <div class="total-cell-dropdown-line"><span class="total-cell-tag">Manual</span> Sales (Man) Taxable: <strong>{{ fmt(sumCol('salesManTaxable')) }}</strong></div>
                    </div>
                  </div>
                </td>
                <td class="num col-sys-hidden">{{ fmt(sumCol('salesSysNoTax')) }}</td>
                <td class="num">{{ fmt(sumCol('salesManNoTax')) }}</td>
                <td class="num total-cell">
                  <div
                    class="total-cell-wrap pinned-breakdown-wrap"
                    @mouseenter="pinBreakdownDropdown"
                    @mouseleave="resetBreakdownDropdown"
                  >
                    <span class="total-cell-value">{{ fmt(sumTotalPair('salesSysNoTax', 'salesManNoTax')) }}</span>
                    <div class="total-cell-dropdown">
                      <div class="total-cell-dropdown-title">Breakdown</div>
                      <div class="total-cell-dropdown-line"><span class="total-cell-tag">System</span> Sales (Sys) Non-Taxable: <strong>{{ fmt(sumCol('salesSysNoTax')) }}</strong></div>
                      <div class="total-cell-dropdown-line"><span class="total-cell-tag">Manual</span> Sales (Man) Non-Taxable: <strong>{{ fmt(sumCol('salesManNoTax')) }}</strong></div>
                    </div>
                  </div>
                </td>
                <td class="num col-sys-hidden">{{ fmt(sumCol('salesSysTaxableSc')) }}</td>
                <td class="num">{{ fmt(sumCol('salesManTaxableSc')) }}</td>
                <td class="num total-cell">
                  <div
                    class="total-cell-wrap pinned-breakdown-wrap"
                    @mouseenter="pinBreakdownDropdown"
                    @mouseleave="resetBreakdownDropdown"
                  >
                    <span class="total-cell-value">{{ fmt(sumTotalPair('salesSysTaxableSc', 'salesManTaxableSc')) }}</span>
                    <div class="total-cell-dropdown">
                      <div class="total-cell-dropdown-title">Breakdown</div>
                      <div class="total-cell-dropdown-line"><span class="total-cell-tag">System</span> Sales (Sys) Taxable SC: <strong>{{ fmt(sumCol('salesSysTaxableSc')) }}</strong></div>
                      <div class="total-cell-dropdown-line"><span class="total-cell-tag">Manual</span> Sales (Man) Taxable SC: <strong>{{ fmt(sumCol('salesManTaxableSc')) }}</strong></div>
                    </div>
                  </div>
                </td>
                <td class="num col-sys-hidden">{{ fmt(sumCol('salesSysNoTaxSc')) }}</td>
                <td class="num">{{ fmt(sumCol('salesManNoTaxSc')) }}</td>
                <td class="num total-cell">
                  <div
                    class="total-cell-wrap pinned-breakdown-wrap"
                    @mouseenter="pinBreakdownDropdown"
                    @mouseleave="resetBreakdownDropdown"
                  >
                    <span class="total-cell-value">{{ fmt(sumTotalPair('salesSysNoTaxSc', 'salesManNoTaxSc')) }}</span>
                    <div class="total-cell-dropdown">
                      <div class="total-cell-dropdown-title">Breakdown</div>
                      <div class="total-cell-dropdown-line"><span class="total-cell-tag">System</span> Sales (Sys) Non-Taxable SC: <strong>{{ fmt(sumCol('salesSysNoTaxSc')) }}</strong></div>
                      <div class="total-cell-dropdown-line"><span class="total-cell-tag">Manual</span> Sales (Man) Non-Taxable SC: <strong>{{ fmt(sumCol('salesManNoTaxSc')) }}</strong></div>
                    </div>
                  </div>
                </td>
                <td class="num">{{ fmt(rows.reduce((a, r) => a + rowTotalBefore(r), 0)) }}</td>
                <td class="num col-sys-hidden">{{ fmt(rows.reduce((a, r) => a + rowVatSys(r), 0)) }}</td>
                <td class="num">{{ fmt(rows.reduce((a, r) => a + rowVatMan(r), 0)) }}</td>
                <td class="num variation-cell">{{ fmt(sumVatManVariationTotal()) }}</td>
                <td class="num total-cell">
                  <div
                    class="total-cell-wrap pinned-breakdown-wrap"
                    @mouseenter="pinBreakdownDropdown"
                    @mouseleave="resetBreakdownDropdown"
                  >
                    <span class="total-cell-value vat-total-value">{{ fmt(sumVatCombinedTotal()) }}</span>
                    <div class="total-cell-dropdown">
                      <div class="total-cell-dropdown-title">Breakdown</div>
                      <div class="total-cell-dropdown-line"><span class="total-cell-tag">System</span> VAT (Sys): <strong>{{ fmt(sumVatSysTotal()) }}</strong></div>
                      <div class="total-cell-dropdown-line"><span class="total-cell-tag">Manual</span> VAT (Man): <strong>{{ fmt(sumVatManTotal()) }}</strong></div>
                    </div>
                  </div>
                </td>
                <td class="num text-slate-700 font-bold">{{ fmt(rows.reduce((a, r) => a + rowTotalSales(r), 0)) }}</td>
                <td class="col-footer-actions">
                  <div
                    class="bank-deposit-footer-wrap"
                    :class="{ 'bank-deposit-footer-wrap--credit': !showBankDepositColumn }"
                  >
                    <button
                      v-if="showBankDepositColumn"
                      type="button"
                      class="bank-action-btn"
                      @click.stop="openBankPanel($event, 'current')"
                    >
                      {{ bankFooterButtonLabel() }} ▾
                    </button>
                    <input
                      v-model="footerRemark"
                      type="text"
                      class="bank-remark-input"
                      placeholder="Remark"
                      maxlength="500"
                    >
                  </div>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>

        <div class="flex justify-center pt-4 pb-1">
          <button
            type="submit"
            :disabled="!canSaveIncome || !canSubmitIncome || isSaving"
            class="min-w-max px-5 py-1.5 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all active:scale-95 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {{ isSaving ? 'Saving…' : (hasExistingIncome ? 'Update Changes' : 'Save') }}
          </button>
        </div>
      </div>
    </form>

    <!-- Saved Records Section -->
    <div class="px-6 pb-12 pt-8 border-t border-slate-100 bg-slate-50/30">
      <div class="flex items-center gap-6 mb-6">
        <div class="flex items-center gap-3 min-w-[220px]">
          <label class="text-[0.78rem] font-semibold text-[#1e293b] shrink-0">
            Start Date <span v-if="activeTab !== 'credit'" class="text-red-500">*</span>
          </label>
          <input
            v-model="filterStart"
            type="date"
            class="flex-1 min-w-[9rem] px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer"
            :required="activeTab !== 'credit'"
          >
        </div>
        <div class="flex items-center gap-3 min-w-[220px]">
          <label class="text-[0.78rem] font-semibold text-[#1e293b] shrink-0">End Date</label>
          <input
            v-model="filterEnd"
            type="date"
            class="flex-1 min-w-[9rem] px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer"
          >
        </div>
        <div v-if="activeTab === 'credit'" class="flex items-center gap-3 min-w-[220px]">
          <label class="text-[0.78rem] font-semibold text-[#1e293b] shrink-0">
            Select Company: <span class="text-red-500">*</span>
          </label>
          <select
            v-model="reportDeliveryCompanyId"
            class="flex-1 min-w-[9rem] px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer hover:bg-slate-50"
          >
            <option value="">Select Company</option>
            <option v-for="dc in deliveryCompanies" :key="'report-' + dc.id" :value="String(dc.id)">{{ dc.name }}</option>
          </select>
        </div>
      </div>

      <div class="relative overflow-auto custom-scrollbar saved-income-report-scroll border border-slate-200 rounded-lg shadow-sm bg-white">
        <table class="w-full today-cash-table min-w-max">
          <thead>
            <tr>
              <th class="col-sn">S/N</th>
              <th class="col-income">Income</th>
              <th class="num col-sys-hidden">Sales<br>(Sys) Taxable</th>
              <th class="num">Sales<br>(Man) Taxable</th>
              <th class="num total-col">Total<br>Taxable</th>
              <th class="num col-sys-hidden">Sales<br>(Sys) Non-Taxable</th>
              <th class="num">Sales<br>(Man) Non-Taxable</th>
              <th class="num total-col">Total<br>Non-Taxable</th>
              <th class="num col-sys-hidden">Sales (Sys)<br>Taxable SC</th>
              <th class="num">Sales (Man)<br>Taxable SC</th>
              <th class="num total-col">Total<br>Taxable SC</th>
              <th class="num col-sys-hidden">Sales (Sys)<br>Non-Taxable SC</th>
              <th class="num">Sales (Man)<br>Non-Taxable SC</th>
              <th class="num total-col">Total<br>Non-Taxable SC</th>
              <th class="num">Total<br>Before</th>
              <th class="num col-sys-hidden">VAT<br>(Sys)</th>
              <th class="num">VAT<br>(Man)</th>
              <th class="num total-col">Total<br>VAT</th>
              <th class="num">Total<br>Sales</th>
              <th v-if="showBankDepositColumn" class="num">Bank Deposit</th>
              <th v-else-if="showCreditPayColumn" class="num">Pay</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, idx) in reportRows" :key="'rep-' + row.incomeCategoryId">
              <td class="col-sn">{{ idx + 1 }}</td>
              <td
                class="col-income"
                :class="{ 'font-bold text-slate-700': isIncomeCategoryRow(row) }"
              >{{ row.categoryName }}</td>
              <td class="num col-sys-hidden text-slate-600">{{ fmt(row.salesSysTaxable) }}</td>
              <td class="num text-slate-600">{{ fmt(row.salesManTaxable) }}</td>
              <td class="num total-cell">
                <div
                  class="total-cell-wrap pinned-breakdown-wrap"
                  @mouseenter="pinBreakdownDropdown"
                  @mouseleave="resetBreakdownDropdown"
                >
                  <span class="total-cell-value">{{ fmt(rowSalesTotal(row, 'taxable', true)) }}</span>
                  <div class="total-cell-dropdown">
                    <div class="total-cell-dropdown-title">Breakdown</div>
                    <div class="total-cell-dropdown-line"><span class="total-cell-tag">System</span> Sales (Sys) Taxable: <strong>{{ fmt(rowSalesSys(row, 'taxable', true)) }}</strong></div>
                    <div class="total-cell-dropdown-line"><span class="total-cell-tag">Manual</span> Sales (Man) Taxable: <strong>{{ fmt(rowSalesMan(row, 'taxable')) }}</strong></div>
                  </div>
                </div>
              </td>
              <td class="num col-sys-hidden text-slate-600">{{ fmt(row.salesSysNoTax) }}</td>
              <td class="num text-slate-600">{{ fmt(row.salesManNoTax) }}</td>
              <td class="num total-cell">
                <div
                  class="total-cell-wrap pinned-breakdown-wrap"
                  @mouseenter="pinBreakdownDropdown"
                  @mouseleave="resetBreakdownDropdown"
                >
                  <span class="total-cell-value">{{ fmt(rowSalesTotal(row, 'nonTaxable', true)) }}</span>
                  <div class="total-cell-dropdown">
                    <div class="total-cell-dropdown-title">Breakdown</div>
                    <div class="total-cell-dropdown-line"><span class="total-cell-tag">System</span> Sales (Sys) Non-Taxable: <strong>{{ fmt(rowSalesSys(row, 'nonTaxable', true)) }}</strong></div>
                    <div class="total-cell-dropdown-line"><span class="total-cell-tag">Manual</span> Sales (Man) Non-Taxable: <strong>{{ fmt(rowSalesMan(row, 'nonTaxable')) }}</strong></div>
                  </div>
                </div>
              </td>
              <td class="num col-sys-hidden text-slate-600">{{ fmt(row.salesSysTaxableSc) }}</td>
              <td class="num text-slate-600">{{ fmt(row.salesManTaxableSc) }}</td>
              <td class="num total-cell">
                <div
                  class="total-cell-wrap pinned-breakdown-wrap"
                  @mouseenter="pinBreakdownDropdown"
                  @mouseleave="resetBreakdownDropdown"
                >
                  <span class="total-cell-value">{{ fmt(rowSalesTotal(row, 'taxableSc', true)) }}</span>
                  <div class="total-cell-dropdown">
                    <div class="total-cell-dropdown-title">Breakdown</div>
                    <div class="total-cell-dropdown-line"><span class="total-cell-tag">System</span> Sales (Sys) Taxable SC: <strong>{{ fmt(rowSalesSys(row, 'taxableSc', true)) }}</strong></div>
                    <div class="total-cell-dropdown-line"><span class="total-cell-tag">Manual</span> Sales (Man) Taxable SC: <strong>{{ fmt(rowSalesMan(row, 'taxableSc')) }}</strong></div>
                  </div>
                </div>
              </td>
              <td class="num col-sys-hidden text-slate-600">{{ fmt(row.salesSysNoTaxSc) }}</td>
              <td class="num text-slate-600">{{ fmt(row.salesManNoTaxSc) }}</td>
              <td class="num total-cell">
                <div
                  class="total-cell-wrap pinned-breakdown-wrap"
                  @mouseenter="pinBreakdownDropdown"
                  @mouseleave="resetBreakdownDropdown"
                >
                  <span class="total-cell-value">{{ fmt(rowSalesTotal(row, 'nonTaxableSc', true)) }}</span>
                  <div class="total-cell-dropdown">
                    <div class="total-cell-dropdown-title">Breakdown</div>
                    <div class="total-cell-dropdown-line"><span class="total-cell-tag">System</span> Sales (Sys) Non-Taxable SC: <strong>{{ fmt(rowSalesSys(row, 'nonTaxableSc', true)) }}</strong></div>
                    <div class="total-cell-dropdown-line"><span class="total-cell-tag">Manual</span> Sales (Man) Non-Taxable SC: <strong>{{ fmt(rowSalesMan(row, 'nonTaxableSc')) }}</strong></div>
                  </div>
                </div>
              </td>
              <td class="num font-bold text-slate-700">{{ fmt(rowTotalBefore(row, true)) }}</td>
              <td class="num col-sys-hidden text-slate-600">{{ fmt(rowVatSys(row, true)) }}</td>
              <td class="num text-slate-600">{{ fmt(rowVatMan(row)) }}</td>
              <td class="num total-cell">
                <div
                  class="total-cell-wrap pinned-breakdown-wrap"
                  @mouseenter="pinBreakdownDropdown"
                  @mouseleave="resetBreakdownDropdown"
                >
                  <span class="total-cell-value">{{ fmt(rowVatTotal(row, true)) }}</span>
                  <div class="total-cell-dropdown">
                    <div class="total-cell-dropdown-title">Breakdown</div>
                    <div class="total-cell-dropdown-line"><span class="total-cell-tag">System</span> VAT (Sys): <strong>{{ fmt(rowVatSys(row, true)) }}</strong></div>
                    <div class="total-cell-dropdown-line"><span class="total-cell-tag">Manual</span> VAT (Man): <strong>{{ fmt(rowVatMan(row)) }}</strong></div>
                  </div>
                </div>
              </td>
              <td class="num font-bold text-slate-700">{{ fmt(rowTotalSales(row, true)) }}</td>
            </tr>
            <tr v-if="reportRows.length === 0">
              <td :colspan="tableColspan" class="py-12 text-center text-slate-400 italic">No records found.</td>
            </tr>
          </tbody>
          <tfoot v-if="reportRows.length > 0">
            <tr class="h-3 border-none bg-transparent"><td :colspan="tableColspan" class="border-none p-0"></td></tr>
            <tr class="footer-total font-semibold bg-slate-100/80 border-t border-slate-200">
              <td colspan="2">
                <button
                  type="button"
                  class="footer-link-btn"
                  @click="openIncomeModalByName('Grand Total')"
                >
                  GRAND TOTAL
                </button>
              </td>
              <td class="num col-sys-hidden">{{ fmt(sumReportCol('salesSysTaxable')) }}</td>
              <td class="num">{{ fmt(sumReportCol('salesManTaxable')) }}</td>
              <td class="num total-cell">
                <div
                  class="total-cell-wrap pinned-breakdown-wrap"
                  @mouseenter="pinBreakdownDropdown"
                  @mouseleave="resetBreakdownDropdown"
                >
                  <span class="total-cell-value">{{ fmt(sumReportTotalPair('salesSysTaxable', 'salesManTaxable')) }}</span>
                  <div class="total-cell-dropdown">
                    <div class="total-cell-dropdown-title">Breakdown</div>
                    <div class="total-cell-dropdown-line"><span class="total-cell-tag">System</span> Sales (Sys) Taxable: <strong>{{ fmt(sumReportCol('salesSysTaxable')) }}</strong></div>
                    <div class="total-cell-dropdown-line"><span class="total-cell-tag">Manual</span> Sales (Man) Taxable: <strong>{{ fmt(sumReportCol('salesManTaxable')) }}</strong></div>
                  </div>
                </div>
              </td>
              <td class="num col-sys-hidden">{{ fmt(sumReportCol('salesSysNoTax')) }}</td>
              <td class="num">{{ fmt(sumReportCol('salesManNoTax')) }}</td>
              <td class="num total-cell">
                <div
                  class="total-cell-wrap pinned-breakdown-wrap"
                  @mouseenter="pinBreakdownDropdown"
                  @mouseleave="resetBreakdownDropdown"
                >
                  <span class="total-cell-value">{{ fmt(sumReportTotalPair('salesSysNoTax', 'salesManNoTax')) }}</span>
                  <div class="total-cell-dropdown">
                    <div class="total-cell-dropdown-title">Breakdown</div>
                    <div class="total-cell-dropdown-line"><span class="total-cell-tag">System</span> Sales (Sys) Non-Taxable: <strong>{{ fmt(sumReportCol('salesSysNoTax')) }}</strong></div>
                    <div class="total-cell-dropdown-line"><span class="total-cell-tag">Manual</span> Sales (Man) Non-Taxable: <strong>{{ fmt(sumReportCol('salesManNoTax')) }}</strong></div>
                  </div>
                </div>
              </td>
              <td class="num col-sys-hidden">{{ fmt(sumReportCol('salesSysTaxableSc')) }}</td>
              <td class="num">{{ fmt(sumReportCol('salesManTaxableSc')) }}</td>
              <td class="num total-cell">
                <div
                  class="total-cell-wrap pinned-breakdown-wrap"
                  @mouseenter="pinBreakdownDropdown"
                  @mouseleave="resetBreakdownDropdown"
                >
                  <span class="total-cell-value">{{ fmt(sumReportTotalPair('salesSysTaxableSc', 'salesManTaxableSc')) }}</span>
                  <div class="total-cell-dropdown">
                    <div class="total-cell-dropdown-title">Breakdown</div>
                    <div class="total-cell-dropdown-line"><span class="total-cell-tag">System</span> Sales (Sys) Taxable SC: <strong>{{ fmt(sumReportCol('salesSysTaxableSc')) }}</strong></div>
                    <div class="total-cell-dropdown-line"><span class="total-cell-tag">Manual</span> Sales (Man) Taxable SC: <strong>{{ fmt(sumReportCol('salesManTaxableSc')) }}</strong></div>
                  </div>
                </div>
              </td>
              <td class="num col-sys-hidden">{{ fmt(sumReportCol('salesSysNoTaxSc')) }}</td>
              <td class="num">{{ fmt(sumReportCol('salesManNoTaxSc')) }}</td>
              <td class="num total-cell">
                <div
                  class="total-cell-wrap pinned-breakdown-wrap"
                  @mouseenter="pinBreakdownDropdown"
                  @mouseleave="resetBreakdownDropdown"
                >
                  <span class="total-cell-value">{{ fmt(sumReportTotalPair('salesSysNoTaxSc', 'salesManNoTaxSc')) }}</span>
                  <div class="total-cell-dropdown">
                    <div class="total-cell-dropdown-title">Breakdown</div>
                    <div class="total-cell-dropdown-line"><span class="total-cell-tag">System</span> Sales (Sys) Non-Taxable SC: <strong>{{ fmt(sumReportCol('salesSysNoTaxSc')) }}</strong></div>
                    <div class="total-cell-dropdown-line"><span class="total-cell-tag">Manual</span> Sales (Man) Non-Taxable SC: <strong>{{ fmt(sumReportCol('salesManNoTaxSc')) }}</strong></div>
                  </div>
                </div>
              </td>
              <td class="num">{{ fmt(reportRows.reduce((a, r) => a + rowTotalBefore(r, true), 0)) }}</td>
              <td class="num col-sys-hidden">{{ fmt(sumReportVatSysTotal()) }}</td>
              <td class="num">{{ fmt(sumReportVatManTotal()) }}</td>
              <td class="num total-cell">
                <div
                  class="total-cell-wrap pinned-breakdown-wrap"
                  @mouseenter="pinBreakdownDropdown"
                  @mouseleave="resetBreakdownDropdown"
                >
                  <span class="total-cell-value">{{ fmt(sumReportVatCombinedTotal()) }}</span>
                  <div class="total-cell-dropdown">
                    <div class="total-cell-dropdown-title">Breakdown</div>
                    <div class="total-cell-dropdown-line"><span class="total-cell-tag">System</span> VAT (Sys): <strong>{{ fmt(sumReportVatSysTotal()) }}</strong></div>
                    <div class="total-cell-dropdown-line"><span class="total-cell-tag">Manual</span> VAT (Man): <strong>{{ fmt(sumReportVatManTotal()) }}</strong></div>
                  </div>
                </div>
              </td>
              <td class="num text-slate-700 font-bold">{{ fmt(grandReportTotalSales) }}</td>
              <td v-if="showCreditPayColumn" class="num text-emerald-700">
                <button
                  type="button"
                  class="bank-action-btn"
                  @click.stop="openBankPanel($event, 'creditPay')"
                >
                  {{ creditPayFooterButtonLabel() }} ▾
                </button>
              </td>
              <td v-if="showBankDepositColumn" class="num text-emerald-700">
                <button
                  type="button"
                  class="bank-action-btn"
                  @click.stop="openBankPanel($event, 'report')"
                >
                  {{ reportBankFooterButtonLabel() }} ▾
                </button>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>

    <FinanceToast :show="toast.show" :variant="toast.variant">{{ toast.message }}</FinanceToast>

    <Teleport to="body">
      <div
        v-if="bankPanel.open"
        ref="bankPanelEl"
        class="fixed z-[9999] flex flex-col bg-white border border-[#cbd5e1] rounded-md shadow-xl text-left max-w-[calc(100vw-1rem)]"
        :class="bankPanel.mode === 'creditPay' ? 'w-[26rem]' : 'w-[20rem]'"
        :style="{ top: bankPanel.top + 'px', left: bankPanel.left + 'px', maxHeight: bankPanel.maxH + 'px' }"
        @click.stop
      >
        <div
          class="shrink-0 px-3 py-2 text-[11px] font-bold text-[#0d4169] border-b border-slate-100"
          :class="bankPanel.mode === 'creditPay' ? 'flex items-center justify-between gap-2' : ''"
        >
          <span>{{ bankPanelTitle() }}</span>
          <span
            v-if="bankPanel.mode === 'creditPay'"
            class="text-[10px] font-semibold text-slate-600 shrink-0 whitespace-nowrap"
          >
        
            <strong class="text-[#0d4169]">{{ fmt(creditPayBalanceRemaining) }}</strong>
          </span>
        </div>
        <div class="min-h-0 flex-1 overflow-y-auto" :class="bankPanel.mode === 'creditPay' ? 'pb-2' : 'pb-10'">
          <template v-if="bankPanel.mode === 'creditPay'">
            <div class="credit-pay-panel-head">
              <span>Posting Date</span>
              <span>Total Sales</span>
              <span>Deposit</span>
            </div>
            <div
              v-for="group in creditPayPostingGroups"
              :key="'credit-pay-' + group.postingDate"
              class="credit-pay-posting-row"
            >
              <span class="text-xs font-semibold text-slate-700">{{ group.postingDate }}</span>
              <span class="text-xs font-bold text-[#0d4169]">{{ fmt(group.totalSales) }}</span>
              <div class="credit-pay-deposit-cell">
                <button
                  type="button"
                  class="credit-pay-deposit-btn"
                  @click.stop="toggleCreditPayDepositDropdown(group.postingDate, $event)"
                >
                  {{ creditPayDepositButtonLabel(group.postingDate) }} ▾
                </button>
              </div>
            </div>
            <p v-if="creditPayPostingGroups.length === 0" class="text-xs text-slate-400 px-3 py-4 text-center">
              No saved posting dates in range.
            </p>
          </template>
          <template v-else>
            <div
              v-for="b in activeBanks"
              :key="b.id"
              class="flex items-center gap-2 px-3 py-1.5 border-b border-slate-50 hover:bg-slate-50"
            >
              <label class="text-xs font-medium text-slate-700 flex-1 truncate">{{ b.bankName }}</label>
              <input
                type="number"
                step="0.01"
                min="0"
                :value="depositAmountFooter(b.id)"
                class="w-24 px-1.5 py-1 border border-[#cbd5e1] rounded outline-none focus:border-[#0d4169] text-xs text-center font-semibold text-[#0d4169]"
                placeholder="0.00"
                :readonly="bankPanel.mode === 'report'"
                @input="setDepositFooter(b.id, $event.target.value)"
              >
            </div>
            <p v-if="activeBanks.length === 0" class="text-xs text-slate-400 px-3 py-4 text-center">No active banks</p>
          </template>
        </div>
        <div
          v-if="bankPanel.mode === 'creditPay'"
          class="shrink-0 px-3 py-2 border-t border-slate-100 flex items-center justify-between gap-2"
        >
          <button
            type="button"
            class="text-[11px] font-bold text-[#2563eb] underline shrink-0"
            @click="closeBankPanel"
          >
            Close
          </button>
          <span
            class="credit-pay-bank-total-wrap text-[10px] text-slate-500 leading-tight text-center flex-1 min-w-0"
            @mouseenter="pinCreditPayHistoryDropdown"
            @mouseleave="resetCreditPayHistoryDropdown"
          >
            Bank total:
            <strong class="text-[#0d4169] credit-pay-bank-total-value">{{ fmt(grandCreditPayTotal()) }}</strong>
            / {{ fmt(grandReportTotalSales) }}
            <div class="credit-pay-history-dropdown">
              <div class="credit-pay-history-dropdown-title">Payment history</div>
              <template v-if="creditPayAllHistory.length">
                <div
                  v-for="(entry, idx) in creditPayAllHistory"
                  :key="`${entry.postingDate}-${entry.bank_id}-${entry.paid_date}-${idx}`"
                  class="credit-pay-history-line"
                >
                  <span class="credit-pay-history-bank">{{ entry.bank_name || 'Bank' }}</span>
                  <strong class="credit-pay-history-amount">{{ fmt(entry.amount) }}</strong>
                  <span class="credit-pay-history-date">{{ formatPaidDate(entry.paid_date) }}</span>
                </div>
              </template>
              <p v-else class="credit-pay-history-empty">No payments yet.</p>
            </div>
          </span>
          <button
            type="button"
            class="px-2.5 py-1 text-[11px] font-bold text-white bg-[#0d4169] rounded hover:bg-[#0a3354] transition-colors disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
            :disabled="isSaving || creditPayExceedsTotalSales()"
            @click="saveCreditPay"
          >
            Save
          </button>
        </div>
        <button
          v-if="bankPanel.mode !== 'creditPay'"
          type="button"
          class="absolute bottom-2 right-2 text-[11px] font-bold text-[#2563eb] underline"
          @click="closeBankPanel"
        >
          Close
        </button>
      </div>
    </Teleport>

    <Teleport to="body">
      <div
        v-if="bankPanel.open && bankPanel.mode === 'creditPay' && creditPayDepositOpen"
        ref="creditPayDepositDropdownEl"
        class="credit-pay-deposit-dropdown credit-pay-deposit-dropdown--overlay"
        :style="creditPayDepositDropdownStyle"
        @click.stop
      >
        <div
          v-for="b in activeBanks"
          :key="`${creditPayDepositOpen}-${b.id}`"
          class="flex items-center gap-2 px-3 py-1.5 border-b border-slate-50 hover:bg-slate-50"
        >
          <label class="text-xs font-medium text-slate-700 flex-1 truncate">{{ b.bankName }}</label>
          <input
            type="number"
            step="0.01"
            min="0"
            :value="depositAmountCreditPay(creditPayDepositOpen, b.id)"
            class="w-24 px-1.5 py-1 border border-[#cbd5e1] rounded outline-none focus:border-[#0d4169] text-xs text-center font-semibold text-[#0d4169]"
            placeholder="0.00"
            @input="setCreditPayAmount(creditPayDepositOpen, b.id, $event.target.value)"
          >
        </div>
        <p v-if="activeBanks.length === 0" class="text-xs text-slate-400 px-3 py-2">No active banks</p>
      </div>
    </Teleport>

    <Teleport to="body">
      <div
        v-if="incomeModal.show"
        class="fixed inset-0 z-[10000] flex items-center justify-center px-4 overflow-y-auto bg-black/50 backdrop-blur-sm"
        @click.self="closeIncomeModal"
      >
        <div
          class="bg-white rounded-xl shadow-2xl w-full max-w-lg my-8 transform transition-all animate-in fade-in zoom-in duration-300 text-left relative overflow-hidden"
        >
          <div class="px-6 py-4 bg-[#0d4169] text-white flex items-center justify-between rounded-t-xl">
            <h2 class="text-lg font-bold m-0">Income Category Details</h2>
            <button
              type="button"
              class="p-1 hover:bg-white/10 rounded-full transition-colors text-white"
              @click="closeIncomeModal"
            >
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div class="p-8">
            <div class="flex flex-col items-center gap-4">
              <div class="w-16 h-16 rounded-full bg-blue-50 flex items-center justify-center text-[#0d4169] mb-2 shadow-inner">
                <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 class="text-base font-bold text-slate-800 uppercase tracking-wide">
                {{ incomeModal.categoryName }}
              </h3>
              <div class="w-full h-px bg-slate-100 my-2"></div>
            </div>
          </div>

          <div class="px-8 py-6 border-t border-slate-100 bg-slate-50 flex justify-end items-center gap-4">
            <button
              type="button"
              class="px-8 py-2 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all active:transform active:scale-95"
              @click="closeIncomeModal"
            >
              Confirm
            </button>
            <button
              type="button"
              class="text-sm font-semibold text-slate-500 hover:text-slate-800 transition-colors"
              @click="closeIncomeModal"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </Teleport>
    </template>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import { useStore } from 'vuex'
import FinanceToast from '../FinanceToast.vue'
import { apiFetch } from '../../../utils/api'

const store = useStore()

const resolvedCompanyName = computed(() => {
  const user = store.getters.currentUser || store.state.user || {}
  return (
    user?.company?.name ||
    user?.companyName ||
    user?.company_name ||
    user?.company?.company_name ||
    'Company Name'
  )
})

const currentUser = computed(() => store.getters.currentUser || store.state.user || {})

const userPrivileges = computed(() => {
  const raw = currentUser.value?.privilege
  if (!raw) return {}
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw)
    } catch {
      return {}
    }
  }
  return raw
})

const hasAllPrivilege = computed(() => {
  return userPrivileges.value?.All === true || userPrivileges.value?.all === true
})

function getPrivilegeEntry(key) {
  const priv = userPrivileges.value || {}
  if (!key) return null
  if (priv?.[key]) return priv[key]

  const normalizedNeedle = String(key).trim().toLowerCase()
  const foundKey = Object.keys(priv).find((k) => String(k).trim().toLowerCase() === normalizedNeedle)
  return foundKey ? priv[foundKey] : null
}

function getActionFlag(entry, action) {
  if (!entry || typeof entry !== 'object') return false
  if (!action) return false
  if (entry[action] !== undefined) return !!entry[action]

  const a = String(action)
  const candidates = [
    a.toLowerCase(),
    a.toUpperCase(),
    a.charAt(0).toUpperCase() + a.slice(1).toLowerCase()
  ]
  for (const k of candidates) {
    if (entry[k] !== undefined) return !!entry[k]
  }
  return false
}

const canAddIncome = computed(() => {
  if (hasAllPrivilege.value) return true
  return getActionFlag(getPrivilegeEntry('Income'), 'Add')
})

const canEditIncome = computed(() => {
  if (hasAllPrivilege.value) return true
  return getActionFlag(getPrivilegeEntry('Income'), 'Edit')
})

const hasExistingIncome = computed(() => {
  return loadedSavedIncomeId.value != null || loadedSavedTotalIncomeId.value != null
})

const cashManualFieldsReadonly = computed(() => {
  return normalizeTab(activeTab.value) === 'cash' && hasExistingIncome.value
})

const canSaveIncome = computed(() => {
  if (hasExistingIncome.value) return canEditIncome.value
  return canAddIncome.value
})

const companyId = computed(() => currentUser.value?.companyId || currentUser.value?.company_id || null)
const branchId = computed(() => currentUser.value?.branchId || currentUser.value?.branch_id || null)

const calendarDate = ref('')
const activeTab = ref('cash')
const filterStart = ref('')
const filterEnd = ref('')
const selectedDeliveryCompanyId = ref('')
const reportDeliveryCompanyId = ref('')
const deliveryCompanies = ref([])

/** Date input must be explicitly selected; no default posting date. */
function effectivePostingDate() {
  return String(calendarDate.value || '').trim()
}

const canSubmitIncome = computed(() => {
  if (!effectivePostingDate()) return false
  if (normalizeTab(activeTab.value) === 'credit' && !String(selectedDeliveryCompanyId.value || '').trim()) {
    return false
  }
  return true
})

const vatPct = ref(15)
const companyConstants = ref({
  tax: 15,
  addition: 10,
})
const rows = ref([])
const reportVersion = ref(0)
const toast = reactive({ show: false, message: '', variant: 'success' })
const bankPanelEl = ref(null)
const creditPayDepositDropdownEl = ref(null)
const creditPayDepositDropdownStyle = ref({})
const pageLoading = ref(true)

const bankPanel = reactive({
  open: false,
  mode: 'current',
  top: 0,
  left: 0,
  maxH: 340
})

const footerBankDeposits = ref({}) // { [bankId]: amount } (cash tab only)
const footerBankDepositsChecked = ref({}) // { [bankId]: 0|1 } preserved from saved bank_deposits
const creditReportPayBanks = ref({}) // { [postingDate]: { [bankId]: amount } } draft amounts only (credit Pay dropdown)
const creditPayDepositOpen = ref(null) // postingDate with open per-row deposit dropdown
const footerRemark = ref('')

const showBankDepositColumn = computed(() => activeTab.value === 'cash')
const showCreditPayColumn = computed(() => activeTab.value === 'credit')
const tableColspan = computed(() => (showBankDepositColumn.value || showCreditPayColumn.value ? 20 : 19))
const formTableColspan = computed(() => 21)

const incomeModal = reactive({
  show: false,
  categoryId: null,
  categoryName: ''
})

const activeBanks = ref([])
const deptSalesById = ref({})
// Category + department catalog for saved-income report labels (independent of form table rows).
const incomeSources = ref([])
// Saved-income report rows: populated only from GET /finance/saved-income (database).
const reportSnapshots = ref([])
const loadedSavedIncomeId = ref(null)
const loadedSavedTotalIncomeId = ref(null)
const isSaving = ref(false)
let hydrateSeq = 0
let reportFetchSeq = 0

function normalizeTab(tab) {
  const k = String(tab || '').trim().toLowerCase()
  return k === 'credit' ? 'credit' : 'cash'
}

async function fetchSavedIncomeRange(startDate, endDate, tab, deliveryId = null, { unpaidOnly = false } = {}) {
  const t = normalizeTab(tab)
  const start = String(startDate || '').trim()
  const end = String(endDate || '').trim() || start
  const deliveryKey = t === 'credit' ? String(deliveryId || '').trim() : ''

  if (t === 'credit') {
    if (!deliveryKey) return []
    if (!unpaidOnly && !start) return []
  } else if (!start) {
    return []
  }

  const qs = new URLSearchParams({ tab: t })
  if (start) qs.set('startDate', start)
  if (end) qs.set('endDate', end)
  if (deliveryKey) qs.set('deliveryId', deliveryKey)
  if (unpaidOnly) qs.set('unpaidOnly', '1')
  const res = await apiFetch(`/finance/saved-income?${qs.toString()}`)
  if (!res.ok) return []
  const list = await res.json().catch(() => [])
  return Array.isArray(list) ? list : []
}

async function refreshReportFromApi() {
  const seq = ++reportFetchSeq
  const tab = normalizeTab(activeTab.value)
  const start = String(filterStart.value || '').trim()
  const end = String(filterEnd.value || '').trim()
  const deliveryId = tab === 'credit' ? String(reportDeliveryCompanyId.value || '').trim() : ''

  if (tab === 'credit') {
    if (!deliveryId) {
      reportSnapshots.value = []
      return
    }
  } else if (!start) {
    reportSnapshots.value = []
    return
  }

  const effectiveEnd = end || start
  const list = await fetchSavedIncomeRange(
    start,
    effectiveEnd,
    tab,
    deliveryId || null,
    { unpaidOnly: tab === 'credit' }
  )
  if (seq !== reportFetchSeq) return
  reportSnapshots.value = list
  syncCreditPayFromSnapshots()
}

function isDepartmentRow(row) {
  const id = row?.incomeCategoryId
  return typeof id === 'string' && id.startsWith('department-')
}

function isIncomeCategoryRow(row) {
  if (row?.categoryType != null) return row.categoryType === 'income-category'
  return !isDepartmentRow(row)
}

function departmentSales(incomeCategoryId) {
  const key = String(incomeCategoryId || '')
  const v = deptSalesById.value[key]
  return v && typeof v === 'object'
    ? v
    : { taxable: 0, nonTaxable: 0, taxableSc: 0, nonTaxableSc: 0, vatSys: 0, totalSales: 0 }
}

function bankRowIsActive(row) {
  if (!row || typeof row !== 'object') return false
  const a = row.active
  return a === true || a === 1 || a === '1'
}

async function loadDeliveryCompanies() {
  try {
    const res = await apiFetch('/settings/delivery-companies')
    if (!res.ok) {
      deliveryCompanies.value = []
      return
    }
    const data = await res.json().catch(() => [])
    deliveryCompanies.value = Array.isArray(data)
      ? data.filter((c) => c.isActive === 1 || c.isActive === true)
      : []
  } catch {
    deliveryCompanies.value = []
  }
}

async function loadActiveBanks() {
  try {
    const res = await apiFetch('/finance/bank-register')
    if (!res.ok) {
      activeBanks.value = []
      return
    }
    const data = await res.json().catch(() => [])
    const list = Array.isArray(data) ? data : []
    activeBanks.value = list
      .filter(bankRowIsActive)
      .map((row) => ({
        id: String(row.id),
        bankName:
          row.bankName != null && String(row.bankName).trim() !== ''
            ? String(row.bankName).trim()
            : '—'
      }))
      .filter((row) => row.id)
  } catch {
    activeBanks.value = []
  }
}

async function loadVatPctFromApi() {
  try {
    const res = await apiFetch('/finance/income-tax')
    if (!res.ok) {
      vatPct.value = 15
      return
    }
    const parsed = await res.json().catch(() => [])
    if (!Array.isArray(parsed)) {
      vatPct.value = 15
      return
    }
    const row = parsed.find((r) => r?.enabled !== false && r?.tax != null && Number(r.tax) >= 0)
    vatPct.value = row ? Number(row.tax) || 15 : 15
  } catch {
    vatPct.value = 15
  }
}

async function loadCompanyConstants() {
  if (!companyId.value) return
  try {
    const res = await apiFetch('/settings/company-constants')
    if (!res.ok) return
    const data = await res.json().catch(() => ({}))
    companyConstants.value = {
      tax: Number(data?.tax ?? 15),
      addition: Number(data?.addition ?? 10),
    }
  } catch {
    // keep defaults
  }
}

function manAdditionRate() {
  return Math.max(0, n(companyConstants.value.addition)) / 100
}

function manTaxRate() {
  return Math.max(0, n(companyConstants.value.tax)) / 100
}

function calcManTaxableSc(taxable) {
  return Math.round(n(taxable) * manAdditionRate() * 100) / 100
}

function calcManNoTaxSc(noTax) {
  return Math.round(n(noTax) * manAdditionRate() * 100) / 100
}

function calcManVat(taxable, taxableSc) {
  const base = n(taxable) + n(taxableSc)
  return Math.round(base * manTaxRate() * 100) / 100
}

/** Form table only: derive manual SC and VAT from company constants. */
function applyManDerivedFields(row) {
  if (!row || typeof row !== 'object') return
  row.salesManTaxableSc = calcManTaxableSc(row.salesManTaxable)
  row.salesManNoTaxSc = calcManNoTaxSc(row.salesManNoTax)
  row.vatMan = calcManVat(row.salesManTaxable, row.salesManTaxableSc)
}

function parseBankDepositsFromFooter(footer) {
  const footerBankDeposits = {}
  const footerBankDepositsChecked = {}
  if (!footer || !Array.isArray(footer.bankDeposits)) {
    return { footerBankDeposits, footerBankDepositsChecked }
  }
  for (const b of footer.bankDeposits) {
    const id = String(b?.bank_id ?? b?.bankId ?? '')
    const amt = Number(b?.amount || 0)
    if (id && Number.isFinite(amt) && amt > 0) {
      footerBankDeposits[id] = (footerBankDeposits[id] || 0) + amt
      const checked = b?.is_checked ?? b?.isChecked
      footerBankDepositsChecked[id] = Number(checked) === 1 || checked === true ? 1 : 0
    }
  }
  return { footerBankDeposits, footerBankDepositsChecked }
}

function getCreditPaySavedHistory(postingDate) {
  const footer = snapshotFooterForPostingDate(postingDate)
  if (!footer || !Array.isArray(footer.bankDeposits)) return []
  return footer.bankDeposits.filter((b) => {
    const id = b?.bank_id ?? b?.bankId
    return id && n(b?.amount) > 0
  })
}

function creditPaySavedTotalForDate(postingDate) {
  return Math.round(getCreditPaySavedHistory(postingDate).reduce((a, b) => a + n(b.amount), 0) * 100) / 100
}

function creditPayDraftTotalForDate(postingDate) {
  const draft = creditReportPayBanks.value[String(postingDate || '')]
  if (!draft || typeof draft !== 'object') return 0
  return Math.round(Object.values(draft).reduce((a, v) => a + n(v), 0) * 100) / 100
}

function formatPaidDate(value) {
  if (!value) return '-'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return String(value)
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}

function parseFooterBankDeposits(footer, forCashTab) {
  if (!forCashTab || !footer || !Array.isArray(footer.bankDeposits)) {
    return { footerBankDeposits: {}, footerBankDepositsChecked: {} }
  }
  return parseBankDepositsFromFooter(footer)
}

function buildBankDepositsPayload(depositsMap, checkedMap = {}) {
  return activeBanks.value
    .map((b) => ({
      bank_id: Number(b.id),
      bank_name: String(b.bankName || '').trim(),
      amount: n(depositsMap[String(b.id)]),
      is_checked: checkedMap[String(b.id)] ?? 0,
    }))
    .filter((b) => b.bank_id && n(b.amount) > 0)
}

function toPostingDateKey(value) {
  if (value == null || value === '') return ''
  const s = String(value).trim()
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s
  const parsed = new Date(s)
  if (!Number.isNaN(parsed.getTime())) {
    const y = parsed.getFullYear()
    const m = String(parsed.getMonth() + 1).padStart(2, '0')
    const d = String(parsed.getDate()).padStart(2, '0')
    return `${y}-${m}-${d}`
  }
  const match = s.match(/^(\d{4}-\d{2}-\d{2})/)
  return match ? match[1] : ''
}

function getSnapshotPostingDate(rec) {
  const d = rec?.postingDate ?? rec?.footer?.postingDate ?? rec?.posting_date
  return toPostingDateKey(d)
}

function computeSnapshotTotalSales(rec) {
  const footerTotal = rec?.footer?.totalSales
  if (footerTotal != null && Number.isFinite(n(footerTotal))) {
    return n(footerTotal)
  }
  const dayList = Array.isArray(rec?.rows) ? rec.rows : []
  return dayList.reduce((a, r) => a + n(r.totalSales ?? r.total_sales), 0)
}

function syncCreditPayFromSnapshots() {
  if (normalizeTab(activeTab.value) !== 'credit') {
    creditReportPayBanks.value = {}
    return
  }
  const next = {}
  for (const rec of reportSnapshots.value) {
    const postingDate = getSnapshotPostingDate(rec)
    if (!postingDate || Object.prototype.hasOwnProperty.call(next, postingDate)) continue
    next[postingDate] = {}
  }
  creditReportPayBanks.value = next
}

const creditPayPostingGroups = computed(() => {
  void reportSnapshots.value
  void activeTab.value
  if (normalizeTab(activeTab.value) !== 'credit') return []
  const byDate = new Map()
  for (const rec of reportSnapshots.value) {
    const postingDate = getSnapshotPostingDate(rec)
    if (!postingDate || byDate.has(postingDate)) continue
    byDate.set(postingDate, {
      postingDate,
      totalSales: computeSnapshotTotalSales(rec),
      savedTotalIncomeId: rec?.savedTotalIncomeId ?? rec?.footer?.id ?? null,
    })
  }
  return Array.from(byDate.values()).sort((a, b) => a.postingDate.localeCompare(b.postingDate))
})

const creditPayAllHistory = computed(() => {
  void reportSnapshots.value
  void reportVersion.value
  const out = []
  for (const group of creditPayPostingGroups.value) {
    for (const entry of getCreditPaySavedHistory(group.postingDate)) {
      out.push({
        postingDate: group.postingDate,
        bank_id: entry?.bank_id ?? entry?.bankId,
        bank_name: String(entry?.bank_name ?? entry?.bankName ?? '').trim(),
        amount: n(entry?.amount),
        paid_date: entry?.paid_date ?? entry?.paidDate ?? null,
      })
    }
  }
  return out.sort((a, b) => String(b.paid_date || '').localeCompare(String(a.paid_date || '')))
})

const creditPayBalanceRemaining = computed(() => {
  void creditReportPayBanks.value
  const totalSales = creditPayPostingGroups.value.reduce((a, g) => a + n(g.totalSales), 0)
  let bankSum = 0
  for (const group of creditPayPostingGroups.value) {
    bankSum += creditPayTotalForDate(group.postingDate)
  }
  return Math.round((totalSales - bankSum) * 100) / 100
})

async function loadEnabledIncomeCategories() {
  try {
    const response = await apiFetch('/finance/income-category')
    if (!response.ok) return []
    const parsed = await response.json().catch(() => [])
    if (!Array.isArray(parsed)) return []
    return parsed.filter((c) => c?.enabled === true || c?.enabled === 1 || c?.status === 1)
  } catch {
    return []
  }
}

function departmentIsActive(dep) {
  if (!dep || typeof dep !== 'object') return false
  if (dep.status != null) return Number(dep.status) === 1
  if (dep.active != null) return dep.active === true || dep.active === 1 || dep.active === '1'
  if (dep.enabled != null) return dep.enabled === true || dep.enabled === 1 || dep.enabled === '1'
  return true
}

async function loadActiveDepartments() {
  if (!companyId.value && !branchId.value) return []
  try {
    let endpoint = `/departments/company/${companyId.value}`
    if (branchId.value) endpoint = `/departments/branch/${branchId.value}`
    const response = await apiFetch(endpoint)
    if (!response.ok) return []
    const parsed = await response.json().catch(() => [])
    const list = Array.isArray(parsed) ? parsed : []
    return list
      .filter(departmentIsActive)
      .map((dep) => ({
        id: `department-${String(dep?.id || '').trim()}`,
        name: String(dep?.name || '').trim()
      }))
      .filter((dep) => dep.id !== 'department-' && dep.name !== '')
  } catch {
    return []
  }
}

async function loadMergedIncomeSources() {
  const [incomeCategories, departments] = await Promise.all([
    loadEnabledIncomeCategories(),
    loadActiveDepartments()
  ])

  const normalizedIncomeCategories = incomeCategories
    .map((cat) => ({
      id: cat.id,
      name: String(cat?.name || '').trim()
    }))
    .filter((cat) => cat.id != null && cat.name !== '')

  const merged = [...normalizedIncomeCategories, ...departments]
  return merged.sort((a, b) => a.name.localeCompare(b.name))
}

function n(v) {
  const x = Number(v)
  return Number.isFinite(x) ? x : 0
}

function fmt(x) {
  return n(x).toFixed(2)
}

function emptyRow(cat) {
  return {
    incomeCategoryId: cat.id,
    categoryName: String(cat.name || '').trim(),
    salesSysTaxable: 0,
    salesManTaxable: 0,
    salesSysNoTax: 0,
    salesManNoTax: 0,
    salesSysTaxableSc: 0,
    salesManTaxableSc: 0,
    salesSysNoTaxSc: 0,
    salesManNoTaxSc: 0,
    vatMan: 0,
    bankDeposits: {}
  }
}

function cloneRowStored(stored, cat) {
  const base = emptyRow(cat)
  if (!stored || typeof stored !== 'object') return base
  
  base.salesSysTaxable = stored.salesSysTaxable ?? 0
  base.salesManTaxable = stored.salesManTaxable ?? (stored.salesTaxable ?? 0)
  
  base.salesSysNoTax = stored.salesSysNoTax ?? 0
  base.salesManNoTax = stored.salesManNoTax ?? (stored.salesNoTax ?? 0)

  // Backward compatibility: old snapshots had a single SC column.
  base.salesSysTaxableSc = stored.salesSysTaxableSc ?? (stored.salesTaxableSc ?? 0)
  base.salesManTaxableSc = stored.salesManTaxableSc ?? 0
  base.salesSysNoTaxSc = stored.salesSysNoTaxSc ?? (stored.salesNoTaxSc ?? 0)
  base.salesManNoTaxSc = stored.salesManNoTaxSc ?? 0
  base.vatMan = stored.vatMan ?? stored.vat_man ?? 0
  base.bankDeposits = normalizeBankDeposits(stored.bankDeposits)
  return base
}

function normalizeBankDeposits(raw) {
  const out = {}
  if (!raw) return out
  if (Array.isArray(raw)) {
    for (const line of raw) {
      const id = String(line?.bank_id ?? line?.bankId ?? '')
      if (!id) continue
      const amt = Number(line?.amount || 0)
      out[id] = Number.isFinite(amt) ? amt : 0
    }
    return out
  }
  if (typeof raw === 'object') {
    for (const k of Object.keys(raw)) {
      const amt = Number(raw[k])
      out[String(k)] = Number.isFinite(amt) ? amt : 0
    }
  }
  return out
}

function rowTotalBefore(row, useStored = false) {
  if (!useStored && isDepartmentRow(row)) {
    const sys = departmentSales(row.incomeCategoryId)
    return (
      n(sys.taxable) + n(row.salesManTaxable) +
      n(sys.nonTaxable) + n(row.salesManNoTax) +
      n(sys.taxableSc) + n(row.salesManTaxableSc) +
      n(sys.nonTaxableSc) + n(row.salesManNoTaxSc)
    )
  }
  if (useStored && row.totalBefore != null && row.totalBefore !== '') {
    return Math.round(n(row.totalBefore) * 100) / 100
  }
  return n(row.salesSysTaxable) + n(row.salesManTaxable) +
         n(row.salesSysNoTax) + n(row.salesManNoTax) +
         n(row.salesSysTaxableSc) + n(row.salesManTaxableSc) +
         n(row.salesSysNoTaxSc) + n(row.salesManNoTaxSc)
}

function rowVatSys(row, useStored = false) {
  if (!useStored && isDepartmentRow(row)) {
    const sys = departmentSales(row.incomeCategoryId)
    return Math.round(n(sys.vatSys) * 100) / 100
  }
  if (useStored && row.vatSys != null && row.vatSys !== '') {
    return Math.round(n(row.vatSys) * 100) / 100
  }
  const base = n(row.salesSysTaxable) + n(row.salesSysTaxableSc)
  return Math.round(base * (n(vatPct.value) / 100) * 100) / 100
}

function rowVatMan(row) {
  return Math.round(n(row.vatMan) * 100) / 100
}

function rowVatManCalculated(row) {
  const taxable = n(row.salesManTaxable)
  const taxableScFromConstants = calcManTaxableSc(taxable)
  return calcManVat(taxable, taxableScFromConstants)
}

/** Form table only: constant-derived VAT (Man) minus manual VAT (Man). */
function rowVatManVariation(row) {
  return Math.round((rowVatManCalculated(row) - rowVatMan(row)) * 100) / 100
}

function sumVatManVariationTotal() {
  return rows.value.reduce((a, r) => a + rowVatManVariation(r), 0)
}

function rowTotalSales(row, useStored = false) {
  if (useStored && row.totalSales != null && row.totalSales !== '') {
    return Math.round(n(row.totalSales) * 100) / 100
  }
  return Math.round((rowTotalBefore(row, useStored) + rowVatSys(row, useStored) + rowVatMan(row)) * 100) / 100
}

const SALES_SYS_FIELDS = {
  taxable: 'salesSysTaxable',
  nonTaxable: 'salesSysNoTax',
  taxableSc: 'salesSysTaxableSc',
  nonTaxableSc: 'salesSysNoTaxSc'
}

const SALES_MAN_FIELDS = {
  taxable: 'salesManTaxable',
  nonTaxable: 'salesManNoTax',
  taxableSc: 'salesManTaxableSc',
  nonTaxableSc: 'salesManNoTaxSc'
}

const DEPT_SYS_FIELDS = {
  taxable: 'taxable',
  nonTaxable: 'nonTaxable',
  taxableSc: 'taxableSc',
  nonTaxableSc: 'nonTaxableSc'
}

function rowSalesSys(row, field, useStored = false) {
  if (!useStored && isDepartmentRow(row)) {
    const sys = departmentSales(row.incomeCategoryId)
    return n(sys[DEPT_SYS_FIELDS[field]])
  }
  return n(row[SALES_SYS_FIELDS[field]])
}

function rowSalesMan(row, field) {
  return n(row[SALES_MAN_FIELDS[field]])
}

function rowSalesTotal(row, field, useStored = false) {
  return rowSalesSys(row, field, useStored) + rowSalesMan(row, field)
}

function rowVatTotal(row, useStored = false) {
  return rowVatSys(row, useStored) + rowVatMan(row)
}

function sumTotalPair(sysProp, manProp) {
  return sumCol(sysProp) + sumCol(manProp)
}

function sumReportTotalPair(sysProp, manProp) {
  return sumReportCol(sysProp) + sumReportCol(manProp)
}

function sumVatSysTotal() {
  return rows.value.reduce((a, r) => a + rowVatSys(r), 0)
}

function sumVatManTotal() {
  return rows.value.reduce((a, r) => a + rowVatMan(r), 0)
}

function sumVatCombinedTotal() {
  return sumVatSysTotal() + sumVatManTotal()
}

function sumReportVatSysTotal() {
  return reportRows.value.reduce((a, r) => a + rowVatSys(r, true), 0)
}

function sumReportVatManTotal() {
  return reportRows.value.reduce((a, r) => a + rowVatMan(r), 0)
}

function sumReportVatCombinedTotal() {
  return sumReportVatSysTotal() + sumReportVatManTotal()
}

const grandBankDeposits = computed(() => {
  let s = 0
  for (const v of Object.values(footerBankDeposits.value)) {
    s += n(v)
  }
  return Math.round(s * 100) / 100
})

const footerTotalSales = computed(() =>
  rows.value.reduce((a, r) => a + rowTotalSales(r), 0)
)

const grandReportTotalSales = computed(() =>
  reportRows.value.reduce((a, r) => a + rowTotalSales(r, true), 0)
)

function grandCreditPayTotal() {
  let s = 0
  for (const group of creditPayPostingGroups.value) {
    s += creditPayTotalForDate(group.postingDate)
  }
  return Math.round(s * 100) / 100
}

function creditPayTotalForDate(postingDate) {
  return Math.round((creditPaySavedTotalForDate(postingDate) + creditPayDraftTotalForDate(postingDate)) * 100) / 100
}

function creditPayExceedsTotalSales() {
  for (const group of creditPayPostingGroups.value) {
    if (creditPayTotalForDate(group.postingDate) > group.totalSales + 0.001) {
      return true
    }
  }
  return false
}

function firstCreditPayExceedingGroup() {
  for (const group of creditPayPostingGroups.value) {
    const payTotal = creditPayTotalForDate(group.postingDate)
    if (payTotal > group.totalSales + 0.001) {
      return { ...group, payTotal }
    }
  }
  return null
}

function bankPanelTitle() {
  if (bankPanel.mode === 'report') return 'Saved bank deposit (amount)'
  if (bankPanel.mode === 'creditPay') return 'Pay by posting date'
  return 'Which bank deposit (amount)'
}

function creditPayFooterButtonLabel() {
  const total = grandCreditPayTotal()
  if (total > 0) return `Pay (${fmt(total)})`
  return 'Pay'
}

function creditPayDepositButtonLabel(postingDate) {
  const total = creditPayTotalForDate(postingDate)
  if (total > 0) return `Edit (${fmt(total)})`
  return 'Choose'
}

function toggleCreditPayDepositDropdown(postingDate, evt) {
  const key = String(postingDate || '')
  if (creditPayDepositOpen.value === key) {
    closeCreditPayDepositDropdown()
    return
  }
  creditPayDepositOpen.value = key
  if (!creditReportPayBanks.value[key] || typeof creditReportPayBanks.value[key] !== 'object') {
    creditReportPayBanks.value = {
      ...creditReportPayBanks.value,
      [key]: {},
    }
  }
  const anchor = evt?.currentTarget
  nextTick(() => {
    nextTick(() => positionCreditPayDepositDropdown(anchor))
  })
}

function positionCreditPayDepositDropdown(anchorEl) {
  const dropdownEl = creditPayDepositDropdownEl.value
  if (!anchorEl || !dropdownEl) return

  const rect = anchorEl.getBoundingClientRect()
  const gap = 4
  const pad = 8
  const dropdownWidth = 224
  const contentHeight = dropdownEl.scrollHeight || 160
  const maxHeight = Math.min(192, contentHeight)
  const dropdownHeight = Math.min(maxHeight, contentHeight)

  let top = rect.top - gap - dropdownHeight
  if (top < pad) {
    top = Math.max(pad, rect.top - gap - maxHeight)
  }

  let left = rect.left + rect.width / 2 - dropdownWidth / 2
  left = Math.max(pad, Math.min(left, window.innerWidth - dropdownWidth - pad))

  creditPayDepositDropdownStyle.value = {
    top: `${top}px`,
    left: `${left}px`,
    width: `${dropdownWidth}px`,
    maxHeight: `${maxHeight}px`,
  }
}

function closeCreditPayDepositDropdown() {
  creditPayDepositOpen.value = null
  creditPayDepositDropdownStyle.value = {}
}

function bankDepositsExceedTotalSales(depositsMap = footerBankDeposits.value) {
  const totalSales = footerTotalSales.value
  let depositSum = 0
  for (const v of Object.values(depositsMap)) {
    depositSum += n(v)
  }
  depositSum = Math.round(depositSum * 100) / 100
  return depositSum > totalSales + 0.001
}

function bankDepositsMatchTotalSales(depositsMap = footerBankDeposits.value) {
  const totalSales = Math.round(footerTotalSales.value * 100) / 100
  let depositSum = 0
  for (const v of Object.values(depositsMap)) {
    depositSum += n(v)
  }
  depositSum = Math.round(depositSum * 100) / 100
  return Math.abs(depositSum - totalSales) <= 0.001
}

function sumCol(prop) {
  return rows.value.reduce((a, r) => {
    if (isDepartmentRow(r)) {
      const sys = departmentSales(r.incomeCategoryId)
      if (prop === 'salesSysTaxable') return a + n(sys.taxable)
      if (prop === 'salesSysNoTax') return a + n(sys.nonTaxable)
      if (prop === 'salesSysTaxableSc') return a + n(sys.taxableSc)
      if (prop === 'salesSysNoTaxSc') return a + n(sys.nonTaxableSc)
    }
    return a + n(r[prop])
  }, 0)
}

function bankFooterButtonLabel() {
  const c = Object.values(footerBankDeposits.value).filter((v) => n(v) > 0).length
  if (c > 0) return `Edit (${fmt(grandBankDeposits.value)})`
  return 'Deposit'
}

function reportBankFooterButtonLabel() {
  const c = Object.values(reportFooterBankDeposits.value).filter((v) => n(v) > 0).length
  if (c > 0) return `Edit (${fmt(grandReportBankDeposits.value)})`
  return 'Deposit'
}

function depositAmountFooter(bankId) {
  let source = footerBankDeposits.value
  if (bankPanel.mode === 'report') source = reportFooterBankDeposits.value
  const v = source[String(bankId)]
  return v !== undefined && n(v) !== 0 ? String(v) : ''
}

function depositAmountCreditPay(postingDate, bankId) {
  const dateKey = String(postingDate || '')
  const banks = creditReportPayBanks.value[dateKey]
  const v = banks?.[String(bankId)]
  return v !== undefined && n(v) !== 0 ? String(v) : ''
}

function setCreditPayAmount(postingDate, bankId, raw) {
  const dateKey = String(postingDate || '')
  const id = String(bankId)
  const group = creditPayPostingGroups.value.find((g) => g.postingDate === dateKey)
  const maxTotal = group ? group.totalSales : 0
  const val = n(raw)
  const currentDateBanks = { ...(creditReportPayBanks.value[dateKey] || {}) }

  if (val <= 0 || raw === '' || raw == null) {
    delete currentDateBanks[id]
    creditReportPayBanks.value = {
      ...creditReportPayBanks.value,
      [dateKey]: currentDateBanks,
    }
    return
  }

  const savedSum = creditPaySavedTotalForDate(dateKey)
  const otherDraftSum = Object.entries(currentDateBanks)
    .filter(([k]) => k !== id)
    .reduce((a, [, v]) => a + n(v), 0)
  const newTotal = Math.round((savedSum + otherDraftSum + val) * 100) / 100
  if (newTotal > maxTotal + 0.001) {
    showToast(
      `Total bank pay (${fmt(newTotal)}) for ${dateKey} cannot exceed Total Sales (${fmt(maxTotal)}).`,
      'error'
    )
    return
  }

  creditReportPayBanks.value = {
    ...creditReportPayBanks.value,
    [dateKey]: { ...currentDateBanks, [id]: val },
  }
}

function setDepositFooter(bankId, raw) {
  if (bankPanel.mode === 'report') return
  const id = String(bankId)
  const val = n(raw)
  if (val <= 0 || raw === '' || raw == null) {
    delete footerBankDeposits.value[id]
    return
  }
  const otherSum = Object.entries(footerBankDeposits.value)
    .filter(([k]) => k !== id)
    .reduce((a, [, v]) => a + n(v), 0)
  const newTotal = Math.round((otherSum + val) * 100) / 100
  if (newTotal > footerTotalSales.value + 0.001) {
    showToast(
      `Total bank deposits (${fmt(newTotal)}) cannot exceed Total Sales (${fmt(footerTotalSales.value)}).`,
      'error'
    )
    return
  }
  footerBankDeposits.value[id] = val
  if (!(id in footerBankDepositsChecked.value)) {
    footerBankDepositsChecked.value = { ...footerBankDepositsChecked.value, [id]: 0 }
  }
}

async function hydrateRows() {
  const seq = ++hydrateSeq
  const date = effectivePostingDate()
  const tab = normalizeTab(activeTab.value)

  if (!date) {
    rows.value = []
    deptSalesById.value = {}
    loadedSavedIncomeId.value = null
    loadedSavedTotalIncomeId.value = null
    footerRemark.value = ''
    footerBankDeposits.value = {}
    footerBankDepositsChecked.value = {}
    pageLoading.value = false
    return
  }

  if (tab === 'credit' && !String(selectedDeliveryCompanyId.value || '').trim()) {
    rows.value = []
    deptSalesById.value = {}
    loadedSavedIncomeId.value = null
    loadedSavedTotalIncomeId.value = null
    footerRemark.value = ''
    pageLoading.value = false
    return
  }

  pageLoading.value = true
  try {
    await loadActiveBanks()
    if (seq !== hydrateSeq) return

    await loadVatPctFromApi()
    if (seq !== hydrateSeq) return

    await loadCompanyConstants()
    if (seq !== hydrateSeq) return

    const cats = await loadMergedIncomeSources()
    if (seq !== hydrateSeq) return
    incomeSources.value = cats

    let savedList = []
    let savedFooterBanks = {}
    let savedFooterBanksChecked = {}

    if (tab === 'cash') {
      const savedDay = await loadSavedIncomeDay(date, tab)
      if (seq !== hydrateSeq) return

      loadedSavedIncomeId.value = savedDay.savedIncomeId ?? null
      loadedSavedTotalIncomeId.value = savedDay.savedTotalIncomeId ?? null
      savedList = savedDay.rows || []
      savedFooterBanks = savedDay.footerBankDeposits || {}
      savedFooterBanksChecked = savedDay.footerBankDepositsChecked || {}
      footerRemark.value = String(savedDay.footer?.remark || '').trim()
    } else {
      // Credit form table: live orders only — do not preload saved_income / saved_total_income.
      loadedSavedIncomeId.value = null
      loadedSavedTotalIncomeId.value = null
      footerRemark.value = ''
    }

    const byCat = new Map((savedList || []).map((s) => [String(s?.incomeCategoryId), s]))

    rows.value = cats.map((cat) => {
      const st = byCat.get(String(cat.id))
      const row = cloneRowStored(st, cat)
      if (!st) applyManDerivedFields(row)
      return row
    }).sort((a, b) => a.categoryName.localeCompare(b.categoryName))

    footerBankDeposits.value = tab === 'cash' ? { ...savedFooterBanks } : {}
    footerBankDepositsChecked.value = tab === 'cash' ? { ...savedFooterBanksChecked } : {}
    await loadDepartmentOrderSalesForDate(
      date,
      tab,
      tab === 'credit' ? selectedDeliveryCompanyId.value : null
    )
    if (seq !== hydrateSeq) return
  } finally {
    if (seq === hydrateSeq) pageLoading.value = false
  }
}

async function loadSavedIncomeDay(date, tab = activeTab.value, deliveryId = null) {
  const d = String(date || '').trim()
  const t = normalizeTab(tab)
  if (!d) return { rows: [], footerBankDeposits: {}, footerBankDepositsChecked: {} }

  const qs = new URLSearchParams({ startDate: d, endDate: d, tab: t })
  const deliveryKey = t === 'credit' ? String(deliveryId || '').trim() : ''
  if (deliveryKey) qs.set('deliveryId', deliveryKey)
  const res = await apiFetch(`/finance/saved-income?${qs.toString()}`)
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error(err.message || 'Failed to load saved income.')
  }
  const list = await res.json().catch(() => [])
  const rec = Array.isArray(list) && list.length ? list[0] : null
  const footer = rec?.footer || null
  const { footerBankDeposits, footerBankDepositsChecked } = parseFooterBankDeposits(footer, t === 'cash')
  return {
    rows: rec?.rows || [],
    footerBankDeposits,
    footerBankDepositsChecked,
    footer,
    tab: t,
    savedIncomeId: rec?.id ?? null,
    savedTotalIncomeId: rec?.savedTotalIncomeId ?? footer?.id ?? null
  }
}

function toNum(v) {
  const x = Number(v)
  return Number.isFinite(x) ? x : 0
}

function normalizeDepartmentKeyFromOrderItem(item) {
  const depId =
    item?.menu?.departmentId ??
    item?.menu?.department_id ??
    item?.departmentId ??
    item?.department_id
  if (depId == null) return ''
  const clean = String(depId).trim()
  return clean ? `department-${clean}` : ''
}

function isDeliveryPayment(item) {
  const pm =
    item?.payment_method ??
    item?.paymentMethod ??
    item?.paymentMethodName ??
    item?.payment_method_name
  if (String(pm || '').toLowerCase() === 'delivery') return true
  if (item?.deliveryId != null && String(item.deliveryId).trim() !== '') return true
  return false
}

function isTaxableItem(item) {
  const vat =
    item?.vat ??
    item?.vat_amount ??
    item?.vatAmount ??
    item?.menu?.vat ??
    item?.menu?.vat_amount
  return toNum(vat) > 0
}

function lineTotalUsingPrice(item) {
  const qty = toNum(item?.quantity ?? item?.qty)
  const price = toNum(item?.price) // requested: use "price" from orders table
  return Math.round(price * qty * 100) / 100
}

function lineTotalUsingAddition(item) {
  const qty = toNum(item?.quantity ?? item?.qty)
  const addition = toNum(item?.addition)
  return Math.round(addition * qty * 100) / 100
}

function lineTotalUsingVat(item) {
  const qty = toNum(item?.quantity ?? item?.qty)
  const vat = toNum(item?.vat)
  return Math.round(vat * qty * 100) / 100
}

function lineTotalUsingTotalPrice(item) {
  const v = toNum(item?.total_price ?? item?.totalPrice ?? item?.total_price)
  return Math.round(v * 100) / 100
}

async function loadDepartmentOrderSalesForDate(date, tab = activeTab.value, deliveryCompanyId = null) {
  // Only for departments; income categories stay manual.
  deptSalesById.value = {}
  if (!companyId.value && !branchId.value) return

  const postingDate = String(date || effectivePostingDate() || '').trim()
  const paymentTab = normalizeTab(tab)
  const deliveryKey = paymentTab === 'credit' ? String(deliveryCompanyId || '').trim() : ''
  if (!postingDate) return
  if (paymentTab === 'credit' && !deliveryKey) return

  try {
    const params = new URLSearchParams()
    if (companyId.value) params.set('companyId', String(companyId.value))
    if (branchId.value) params.set('branchId', String(branchId.value))
    params.set('startDate', postingDate)
    params.set('endDate', postingDate)
    params.set('isPaid', '1')
    params.set('isDeleted', '0')
    params.set('isForStaff', 'Normal')

    const res = await apiFetch(`/orders?${params.toString()}`)
    if (!res.ok) return
    const data = await res.json().catch(() => [])
    const list = Array.isArray(data) ? data : []

    const out = {}
    for (const item of list) {
      // Payment method split: Delivery -> credit tab, else cash tab
      const isDel = isDeliveryPayment(item)
      if (paymentTab === 'cash' && isDel) continue
      if (paymentTab === 'credit' && !isDel) continue

      if (paymentTab === 'credit') {
        const itemDeliveryId = String(item?.deliveryId ?? item?.delivery_id ?? '').trim()
        if (itemDeliveryId !== deliveryKey) continue
      }

      const depKey = normalizeDepartmentKeyFromOrderItem(item)
      if (!depKey) continue

      if (!out[depKey]) out[depKey] = { taxable: 0, nonTaxable: 0, taxableSc: 0, nonTaxableSc: 0, vatSys: 0, totalSales: 0 }

      const amount = lineTotalUsingPrice(item)
      const scAmount = lineTotalUsingAddition(item)
      const vatAmount = lineTotalUsingVat(item)
      const totalSalesAmount = lineTotalUsingTotalPrice(item)

      if (isTaxableItem(item)) {
        out[depKey].taxable += amount
        out[depKey].taxableSc += scAmount
      } else {
        out[depKey].nonTaxable += amount
        out[depKey].nonTaxableSc += scAmount
      }

      out[depKey].vatSys += vatAmount
      out[depKey].totalSales += totalSalesAmount
    }

    // Round once at the end
    for (const k of Object.keys(out)) {
      out[k].taxable = Math.round(toNum(out[k].taxable) * 100) / 100
      out[k].nonTaxable = Math.round(toNum(out[k].nonTaxable) * 100) / 100
      out[k].taxableSc = Math.round(toNum(out[k].taxableSc) * 100) / 100
      out[k].nonTaxableSc = Math.round(toNum(out[k].nonTaxableSc) * 100) / 100
      out[k].vatSys = Math.round(toNum(out[k].vatSys) * 100) / 100
      out[k].totalSales = Math.round(toNum(out[k].totalSales) * 100) / 100
    }
    deptSalesById.value = out
  } catch {
    deptSalesById.value = {}
  }
}

async function onCalendarChange() {
  closeBankPanel()
  filterStart.value = calendarDate.value
  filterEnd.value = ''
  await hydrateRows()
  await refreshReportFromApi()
}

async function onDeliveryCompanyChange() {
  closeBankPanel()
  await hydrateRows()
}

async function switchTab(tab) {
  const next = normalizeTab(tab)
  const prev = activeTab.value
  activeTab.value = next
  closeBankPanel()
  if (next === 'credit' && prev !== 'credit') {
    selectedDeliveryCompanyId.value = ''
    reportDeliveryCompanyId.value = ''
  }
  await hydrateRows()
  await refreshReportFromApi()
}

function showToast(message, variant = 'success') {
  toast.message = message
  toast.variant = variant
  toast.show = true
  window.setTimeout(() => {
    toast.show = false
    toast.variant = 'success'
  }, 3000)
}

function serializeRow(row) {
  const dept = isDepartmentRow(row)
  const sys = dept ? departmentSales(row.incomeCategoryId) : null
  return {
    incomeCategoryId: row.incomeCategoryId,
    categoryType: dept ? 'department' : 'income-category',
    tab: normalizeTab(activeTab.value),
    // Save the values as they appear in the form table (department sys values included).
    salesSysTaxable: dept ? n(sys.taxable) : n(row.salesSysTaxable),
    salesManTaxable: n(row.salesManTaxable),
    salesSysNoTax: dept ? n(sys.nonTaxable) : n(row.salesSysNoTax),
    salesManNoTax: n(row.salesManNoTax),
    salesSysTaxableSc: dept ? n(sys.taxableSc) : n(row.salesSysTaxableSc),
    salesManTaxableSc: n(row.salesManTaxableSc),
    salesSysNoTaxSc: dept ? n(sys.nonTaxableSc) : n(row.salesSysNoTaxSc),
    salesManNoTaxSc: n(row.salesManNoTaxSc),
    vatSys: dept ? n(sys.vatSys) : n(rowVatSys(row)),
    vatMan: n(row.vatMan),
    totalBefore: n(rowTotalBefore(row)),
    totalSales: n(rowTotalSales(row)),
    bankDeposits: []
  }
}

async function save() {
  if (isSaving.value) return
  if (hasExistingIncome.value) {
    if (!canEditIncome.value) return
  } else if (!canAddIncome.value) {
    return
  }
  if (!String(calendarDate.value || '').trim()) {
    showToast('Please select a date.', 'error')
    return
  }
  const tab = normalizeTab(activeTab.value)
  if (tab === 'credit' && !String(selectedDeliveryCompanyId.value || '').trim()) {
    showToast('Please select a delivery company.', 'error')
    return
  }
  if (tab === 'cash' && bankDepositsExceedTotalSales()) {
    showToast(
      `Total bank deposits (${fmt(grandBankDeposits.value)}) cannot exceed Total Sales (${fmt(footerTotalSales.value)}).`,
      'error'
    )
    return
  }
  if (tab === 'cash' && !bankDepositsMatchTotalSales()) {
    showToast(
      `Total bank deposits (${fmt(grandBankDeposits.value)}) must equal Total Sales (${fmt(footerTotalSales.value)}).`,
      'error'
    )
    return
  }
  const snapshot = rows.value.map(serializeRow)
  const wasExisting = loadedSavedIncomeId.value != null || loadedSavedTotalIncomeId.value != null
  isSaving.value = true
  try {
    const footer = {
      salesSysTaxable: sumCol('salesSysTaxable'),
      salesManTaxable: sumCol('salesManTaxable'),
      salesSysNoTax: sumCol('salesSysNoTax'),
      salesManNoTax: sumCol('salesManNoTax'),
      salesSysTaxableSc: sumCol('salesSysTaxableSc'),
      salesManTaxableSc: sumCol('salesManTaxableSc'),
      salesSysNoTaxSc: sumCol('salesSysNoTaxSc'),
      salesManNoTaxSc: sumCol('salesManNoTaxSc'),
      totalBefore: rows.value.reduce((a, r) => a + rowTotalBefore(r), 0),
      vatSys: rows.value.reduce((a, r) => a + rowVatSys(r), 0),
      vatMan: rows.value.reduce((a, r) => a + rowVatMan(r), 0),
      totalSales: rows.value.reduce((a, r) => a + rowTotalSales(r), 0),
      bankDeposits: tab === 'cash'
        ? buildBankDepositsPayload(footerBankDeposits.value, footerBankDepositsChecked.value)
        : [],
      remark: String(footerRemark.value || '').trim()
    }

    const payload = {
      postingDate: calendarDate.value,
      tab,
      rows: snapshot,
      footer,
    }
    if (tab === 'credit') {
      payload.deliveryId = String(selectedDeliveryCompanyId.value)
    }

    const res = await apiFetch('/finance/saved-income', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      throw new Error(err.message || 'Failed to save')
    }

    const saved = await res.json().catch(() => ({}))
    loadedSavedIncomeId.value = saved?.id ?? loadedSavedIncomeId.value
    loadedSavedTotalIncomeId.value =
      saved?.savedTotalIncomeId ?? saved?.footer?.id ?? loadedSavedTotalIncomeId.value

    const isUpdate = saved?.updated === true || wasExisting
    reportVersion.value++
    await hydrateRows()
    await refreshReportFromApi()
    const tabLabel = tab === 'credit' ? 'Credit' : 'Cash'
    showToast(
      isUpdate
        ? `${tabLabel} income updated successfully.`
        : `${tabLabel} income saved successfully.`
    )
  } catch (e) {
    showToast(e?.message || 'Failed to save income.', 'error')
  } finally {
    isSaving.value = false
  }
}

const BANK_PANEL_PAD = 8
const BANK_PANEL_GAP = 6
const BANK_PANEL_WIDTH = 320
const BANK_PANEL_CREDIT_PAY_WIDTH = 420
const BANK_PANEL_MAX_H = 400
const BANK_PANEL_MIN_H = 160

function openBankPanel(evt, mode = 'current') {
  const panelMode = mode === 'report' ? 'report' : mode === 'creditPay' ? 'creditPay' : 'current'
  if (panelMode === 'creditPay' && activeBanks.value.length === 0) {
    void loadActiveBanks()
  }
  if (panelMode === 'creditPay') {
    closeCreditPayDepositDropdown()
    syncCreditPayFromSnapshots()
  }
  const rect = evt.currentTarget.getBoundingClientRect()
  bankPanel.mode = panelMode
  bankPanel.open = true

  const panelWidth = panelMode === 'creditPay' ? BANK_PANEL_CREDIT_PAY_WIDTH : BANK_PANEL_WIDTH
  const spaceBelow = window.innerHeight - rect.bottom - BANK_PANEL_PAD
  const spaceAbove = rect.top - BANK_PANEL_PAD
  const openAbove = spaceBelow < BANK_PANEL_MIN_H && spaceAbove > spaceBelow
  const available = Math.max(
    BANK_PANEL_MIN_H,
    (openAbove ? spaceAbove : spaceBelow) - BANK_PANEL_GAP
  )

  bankPanel.maxH = Math.min(BANK_PANEL_MAX_H, available)
  bankPanel.left = Math.min(
    Math.max(BANK_PANEL_PAD, rect.left),
    window.innerWidth - panelWidth - BANK_PANEL_PAD
  )
  bankPanel.top = openAbove
    ? Math.max(BANK_PANEL_PAD, rect.top - BANK_PANEL_GAP - bankPanel.maxH)
    : rect.bottom + BANK_PANEL_GAP

  bankPanel._anchorRect = { top: rect.top, bottom: rect.bottom }

  nextTick(() => {
    clampBankPanelToViewport()
    bindOutsideClose()
  })
}

function clampBankPanelToViewport() {
  const el = bankPanelEl.value
  if (!el) return

  const anchor = bankPanel._anchorRect
  const panelH = el.offsetHeight
  const viewportBottom = window.innerHeight - BANK_PANEL_PAD
  let top = el.getBoundingClientRect().top

  if (top + panelH > viewportBottom) {
    top = viewportBottom - panelH
  }
  if (top < BANK_PANEL_PAD) {
    top = BANK_PANEL_PAD
    bankPanel.maxH = Math.max(BANK_PANEL_MIN_H, viewportBottom - BANK_PANEL_PAD)
  }

  if (anchor && top + panelH > viewportBottom && anchor.top > panelH + BANK_PANEL_GAP) {
    top = anchor.top - panelH - BANK_PANEL_GAP
    if (top < BANK_PANEL_PAD) top = BANK_PANEL_PAD
  }

  bankPanel.top = top
}

function closeBankPanel() {
  closeCreditPayDepositDropdown()
  bankPanel.open = false
  bankPanel.mode = 'current'
  bankPanel._anchorRect = null
  removeOutsideClose()
}

function snapshotFooterForPostingDate(postingDate) {
  const dateKey = String(postingDate || '')
  const rec = reportSnapshots.value.find((row) => getSnapshotPostingDate(row) === dateKey)
  return rec?.footer || null
}

async function saveCreditPay() {
  if (isSaving.value) return

  const exceeded = firstCreditPayExceedingGroup()
  if (exceeded) {
    showToast(
      `Total bank pay (${fmt(exceeded.payTotal)}) for ${exceeded.postingDate} cannot exceed Total Sales (${fmt(exceeded.totalSales)}).`,
      'error'
    )
    return
  }

  const deliveryId = String(reportDeliveryCompanyId.value || '').trim()
  if (!deliveryId) {
    showToast('Please select a delivery company.', 'error')
    return
  }

  const items = []
  for (const group of creditPayPostingGroups.value) {
    const banksMap = creditReportPayBanks.value[group.postingDate] || {}
    const bankDeposits = buildBankDepositsPayload(banksMap, {})
    if (bankDeposits.length === 0) continue

    const savedTotalIncomeId = group.savedTotalIncomeId
    if (!savedTotalIncomeId) {
      showToast(`No saved total income record for ${group.postingDate}.`, 'error')
      return
    }

    items.push({
      postingDate: group.postingDate,
      savedTotalIncomeId,
      bankDeposits,
    })
  }

  if (items.length === 0) {
    showToast('Enter at least one bank deposit amount.', 'error')
    return
  }

  isSaving.value = true
  try {
    const res = await apiFetch('/finance/saved-income/credit-pay-bank-deposits', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        tab: 'credit',
        deliveryId,
        items,
      }),
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      throw new Error(err.message || 'Failed to save credit pay bank deposits.')
    }

    reportVersion.value++
    await refreshReportFromApi()
    closeBankPanel()
    showToast('Credit pay bank deposits saved successfully.')
  } catch (e) {
    showToast(e?.message || 'Failed to save credit pay bank deposits.', 'error')
  } finally {
    isSaving.value = false
  }
}

function pinBreakdownDropdown(evt) {
  const wrap = evt.currentTarget
  const dd = wrap?.querySelector('.total-cell-dropdown')
  if (!dd) return
  const rect = wrap.getBoundingClientRect()
  const gap = 4
  dd.style.visibility = 'hidden'
  dd.style.display = 'block'
  dd.style.position = 'fixed'
  const h = dd.offsetHeight || 72
  const spaceBelow = window.innerHeight - rect.bottom - gap
  dd.style.left = `${rect.left + rect.width / 2}px`
  dd.style.transform = 'translateX(-50%)'
  dd.style.zIndex = '10000'
  dd.style.visibility = 'visible'
  if (spaceBelow < h) {
    dd.style.top = `${Math.max(gap, rect.top - h - gap)}px`
  } else {
    dd.style.top = `${rect.bottom + gap}px`
  }
}

function resetBreakdownDropdown(evt) {
  const wrap = evt.currentTarget
  const dd = wrap?.querySelector('.total-cell-dropdown')
  if (!dd) return
  dd.style.position = ''
  dd.style.left = ''
  dd.style.top = ''
  dd.style.transform = ''
  dd.style.zIndex = ''
  dd.style.visibility = ''
  dd.style.display = ''
}

function pinCreditPayHistoryDropdown(evt) {
  const wrap = evt.currentTarget
  const dd = wrap?.querySelector('.credit-pay-history-dropdown')
  if (!dd) return
  const rect = wrap.getBoundingClientRect()
  const gap = 4
  dd.style.visibility = 'hidden'
  dd.style.display = 'block'
  dd.style.position = 'fixed'
  const h = dd.offsetHeight || 120
  const spaceBelow = window.innerHeight - rect.bottom - gap
  dd.style.left = `${rect.left + rect.width / 2}px`
  dd.style.transform = 'translateX(-50%)'
  dd.style.zIndex = '10002'
  dd.style.visibility = 'visible'
  if (spaceBelow < h) {
    dd.style.top = `${Math.max(gap, rect.top - h - gap)}px`
  } else {
    dd.style.top = `${rect.bottom + gap}px`
  }
}

function resetCreditPayHistoryDropdown(evt) {
  const wrap = evt.currentTarget
  const dd = wrap?.querySelector('.credit-pay-history-dropdown')
  if (!dd) return
  dd.style.position = ''
  dd.style.left = ''
  dd.style.top = ''
  dd.style.transform = ''
  dd.style.zIndex = ''
  dd.style.display = ''
  dd.style.visibility = ''
}

const reportRows = computed(() => {
  void reportVersion.value
  void activeTab.value
  void reportSnapshots.value
  void incomeSources.value
  void reportDeliveryCompanyId.value
  const tab = normalizeTab(activeTab.value)
  if (tab === 'cash' && !filterStart.value) return []
  if (tab === 'credit' && !String(reportDeliveryCompanyId.value || '').trim()) return []

  const aggregated = new Map()

  for (const rec of reportSnapshots.value) {
    const dayList = Array.isArray(rec?.rows) ? rec.rows : []
    if (dayList.length === 0) continue

    for (const r of dayList) {
      const id = String(r.incomeCategoryId)
      if (!aggregated.has(id)) {
        const savedName = String(r.categoryName || '').trim()
        aggregated.set(id, {
          incomeCategoryId: r.incomeCategoryId,
          categoryType: r.categoryType || (String(id).startsWith('department-') ? 'department' : 'income-category'),
          categoryName: savedName || ('Category ' + id),
          salesSysTaxable: 0,
          salesManTaxable: 0,
          salesSysNoTax: 0,
          salesManNoTax: 0,
          salesSysTaxableSc: 0,
          salesManTaxableSc: 0,
          salesSysNoTaxSc: 0,
          salesManNoTaxSc: 0,
          vatSys: 0,
          vatMan: 0,
          totalBefore: 0,
          totalSales: 0
        })
      }

      const target = aggregated.get(id)
      target.salesSysTaxable += n(r.salesSysTaxable)
      target.salesManTaxable += n(r.salesManTaxable)
      target.salesSysNoTax += n(r.salesSysNoTax)
      target.salesManNoTax += n(r.salesManNoTax)
      target.salesSysTaxableSc += n(r.salesSysTaxableSc ?? r.salesTaxableSc)
      target.salesManTaxableSc += n(r.salesManTaxableSc)
      target.salesSysNoTaxSc += n(r.salesSysNoTaxSc ?? r.salesNoTaxSc)
      target.salesManNoTaxSc += n(r.salesManNoTaxSc)
      target.vatSys += n(r.vatSys ?? r.vat_sys)
      target.vatMan += n(r.vatMan ?? r.vat_man)
      target.totalBefore += n(r.totalBefore ?? r.total_before)
      target.totalSales += n(r.totalSales ?? r.total_sales)
    }
  }

  const nameMap = new Map(incomeSources.value.map((c) => [String(c.id), c.name]))

  const result = Array.from(aggregated.values()).map(r => ({
    ...r,
    categoryName: nameMap.get(String(r.incomeCategoryId)) || r.categoryName
  }))

  return result.sort((a, b) => a.categoryName.localeCompare(b.categoryName))
})

function sumReportCol(prop) {
  return reportRows.value.reduce((a, r) => a + n(r[prop]), 0)
}

const reportFooterBankDeposits = computed(() => {
  void reportVersion.value
  void activeTab.value
  void reportSnapshots.value
  if (!filterStart.value || normalizeTab(activeTab.value) !== 'cash') return {}
  const merged = {}
  for (const rec of reportSnapshots.value) {
    const { footerBankDeposits: dayFooter } = parseFooterBankDeposits(rec?.footer, true)
    for (const [bankId, amount] of Object.entries(dayFooter)) {
      merged[String(bankId)] = (merged[String(bankId)] || 0) + n(amount)
    }
  }
  return merged
})

const grandReportBankDeposits = computed(() => {
  let total = 0
  for (const v of Object.values(reportFooterBankDeposits.value)) {
    total += n(v)
  }
  return Math.round(total * 100) / 100
})

function openIncomeModalByName(name) {
  incomeModal.categoryId = null
  incomeModal.categoryName = String(name || '').trim() || 'Income'
  incomeModal.show = true
}

function closeIncomeModal() {
  incomeModal.show = false
  incomeModal.categoryId = null
  incomeModal.categoryName = ''
}

let outsideHandler = null
function bindOutsideClose() {
  removeOutsideClose()
  outsideHandler = (e) => {
    const panelEl = bankPanelEl.value
    const depositEl = creditPayDepositDropdownEl.value
    if (depositEl && depositEl.contains(e.target)) return
    if (panelEl && panelEl.contains(e.target)) {
      if (creditPayDepositOpen.value) {
        closeCreditPayDepositDropdown()
      }
      return
    }
    closeBankPanel()
  }
  document.addEventListener('pointerdown', outsideHandler)
}

function removeOutsideClose() {
  if (outsideHandler) {
    document.removeEventListener('pointerdown', outsideHandler)
    outsideHandler = null
  }
}

async function onWindowFocus() {
  await loadActiveBanks()
  await loadVatPctFromApi()
  await loadCompanyConstants()
  incomeSources.value = await loadMergedIncomeSources()
}

onMounted(async () => {
  await Promise.all([
    loadDeliveryCompanies(),
    loadMergedIncomeSources().then((list) => {
      incomeSources.value = list
    })
  ])
  pageLoading.value = false
  window.addEventListener('focus', onWindowFocus)
})

watch([filterStart, filterEnd, activeTab, reportDeliveryCompanyId, reportVersion], () => {
  refreshReportFromApi()
}, { immediate: true })

onBeforeUnmount(() => {
  removeOutsideClose()
  window.removeEventListener('focus', onWindowFocus)
})
</script>

<style scoped>
/* Main Table Layout */
.today-cash-table {
  font-family: 'Segoe UI', sans-serif;
  font-size: 0.75rem;
  color: #1e293b;
  border-collapse: separate;
  border-spacing: 0;
  width: auto;
  min-width: 100%;
}

.today-cash-table th,
.today-cash-table td {
  padding: 4px 10px;
  text-align: center;
  border-bottom: 1px solid #e2e8f0;
  vertical-align: middle;
  font-size: 0.72rem;
  line-height: 1.2;
}

/* Header Stickiness (Vertical) */
.today-cash-table th {
  background: #f1f5f9;
  font-weight: 600;
  position: sticky;
  top: 0;
  z-index: 20;
}

/* Horizontal Sticky Columns (S/N and Income) */
.col-sn, .col-income {
  position: sticky !important;
  z-index: 10;
  background-color: white !important;
}

.col-sn {
  left: 0;
  width: 2.25rem;
  min-width: 2.25rem;
  text-align: center;
}

.col-income {
  left: 2.25rem;
  min-width: 6rem;
  text-align: left !important;
  white-space: nowrap;
}

/* Corner Stickiness (Header cells for sticky columns) */
.today-cash-table thead th.col-sn,
.today-cash-table thead th.col-income {
  z-index: 30;
  background-color: #f1f5f9 !important;
}

.today-cash-table thead th.col-sn { left: 0; }
.today-cash-table thead th.col-income { left: 2.25rem; }

/* Row Hover with Sticky Background Support */
.today-cash-table tr:hover td {
  background-color: #f8fafc;
}
.today-cash-table tr:hover td.col-sn,
.today-cash-table tr:hover td.col-income {
  background-color: #f8fafc !important;
}

/* Numeric Columns */
.today-cash-table th.num,
.today-cash-table td.num {
  width: 5.5rem;
  min-width: 5.5rem;
  text-align: center;
  white-space: nowrap;
}

/* Input Fields */
.today-cash-table input.income-table-field {
  box-sizing: border-box;
  width: 100%;
  padding: 2px 4px;
  border: 1px solid transparent;
  border-radius: 3px;
  outline: none;
  background-color: #fef2f2;
  font-size: 0.72rem;
  text-align: center;
  font-variant-numeric: tabular-nums;
  transition: all 0.15s;
}

.today-cash-table input.income-table-field:focus {
  border-color: #0d4169;
  background-color: #fff;
}

.today-cash-table input.income-table-field[readonly] {
  background-color: #f8fafc;
  cursor: default;
}

/* Action Buttons */
.bank-action-btn {
  width: 100%;
  padding: 2px 6px;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  font-size: 0.68rem;
  font-weight: 600;
  color: #0d4169;
  background: #fff;
  cursor: pointer;
  transition: all 0.15s;
}
.bank-action-btn:hover { border-color: #0d4169; }

.income-cat-btn {
  background: none;
  border: none;
  padding: 0;
  color: #2563eb;
  text-decoration: underline;
  cursor: pointer;
  text-align: left;
  transition: color 0.15s;
}
.income-cat-btn:hover { color: #0d4169; }

.footer-link-btn {
  display: inline;
  width: auto;
  max-width: max-content;
  background: none;
  border: none;
  padding: 0;
  color: #2563eb;
  font-weight: inherit;
  text-decoration: underline;
  cursor: pointer;
  transition: color 0.15s;
}

.footer-link-btn:hover {
  color: #0d4169;
}

/* Custom Scrollbar Styling */
.custom-scrollbar::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: #f1f5f9;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 10px;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* Layout adjustments */
@media (min-width: 1024px) {
  .today-cash-table {
    table-layout: auto;
  }
  .col-income {
    min-width: max(6rem, max-content);
  }
}

.col-footer-actions {
  white-space: nowrap;
  min-width: 18rem;
  width: 18rem;
  text-align: left;
  vertical-align: middle;
}

.bank-deposit-footer-wrap {
  display: grid;
  grid-template-columns: 6.5rem 11rem;
  gap: 0.5rem;
  align-items: center;
  width: max-content;
}

.bank-deposit-footer-wrap--credit {
  grid-template-columns: 11rem;
}

.credit-pay-panel-head {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.35rem;
  padding: 0.5rem 0.75rem;
  font-size: 10px;
  font-weight: 700;
  color: #64748b;
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
  position: sticky;
  top: 0;
  z-index: 1;
}

.credit-pay-panel-head > span:first-child {
  text-align: left;
}

.credit-pay-panel-head > span:nth-child(2),
.credit-pay-panel-head > span:nth-child(3) {
  text-align: center;
}

.credit-pay-posting-row {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.35rem;
  align-items: center;
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid #e2e8f0;
}

.credit-pay-posting-row > span:first-child {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: left;
}

.credit-pay-posting-row > span:nth-child(2) {
  text-align: center;
}

.credit-pay-deposit-cell {
  position: relative;
  width: 100%;
  min-width: 0;
}

.credit-pay-deposit-btn {
  width: 100%;
  padding: 2px 6px;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  font-size: 0.68rem;
  font-weight: 600;
  color: #0d4169;
  background: #fff;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
}

.credit-pay-deposit-btn:hover {
  border-color: #0d4169;
}

.credit-pay-deposit-dropdown {
  min-width: 14rem;
  background: #fff;
  border: 1px solid #cbd5e1;
  border-radius: 0.375rem;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.12);
  overflow-y: auto;
}

.credit-pay-deposit-dropdown--overlay {
  position: fixed;
  z-index: 10001;
}

.credit-pay-bank-total-wrap {
  position: relative;
  cursor: help;
}

.credit-pay-bank-total-value {
  border-bottom: 1px dotted #94a3b8;
}

.credit-pay-history-dropdown {
  display: none;
  position: absolute;
  top: calc(100% + 4px);
  left: 50%;
  transform: translateX(-50%);
  z-index: 60;
  min-width: 16rem;
  max-width: 20rem;
  max-height: 14rem;
  overflow-y: auto;
  padding: 8px 10px;
  background: #fff;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.12);
  text-align: left;
}

.credit-pay-bank-total-wrap:hover .credit-pay-history-dropdown {
  display: block;
}

.credit-pay-history-dropdown-title {
  font-size: 10px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 6px;
}

.credit-pay-history-line {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  gap: 0.5rem;
  align-items: center;
  font-size: 11px;
  color: #334155;
  padding: 3px 0;
  border-bottom: 1px solid #f1f5f9;
}

.credit-pay-history-line:last-child {
  border-bottom: none;
}

.credit-pay-history-bank {
  font-weight: 600;
  color: #0d4169;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.credit-pay-history-amount {
  font-variant-numeric: tabular-nums;
  color: #0d4169;
}

.credit-pay-history-date {
  font-size: 10px;
  color: #64748b;
  white-space: nowrap;
}

.credit-pay-history-empty {
  font-size: 11px;
  color: #94a3b8;
  font-style: italic;
  margin: 0;
}

.bank-remark-input {
  width: 100%;
  min-width: 11rem;
  box-sizing: border-box;
  padding: 0.25rem 0.5rem;
  border: 1px solid #cbd5e1;
  border-radius: 0.375rem;
  outline: none;
  font-size: 0.75rem;
  color: #1e293b;
}

.bank-remark-input:focus {
  border-color: #0d4169;
}

.today-cash-table th.total-col,
.today-cash-table td.total-cell {
  background-color: #f8fafc;
}

/* Display-only: system columns stay in DOM for save/calc; hidden from view */
.today-cash-table .col-sys-hidden {
  display: none;
}

.today-cash-table tr:hover td.total-cell {
  background-color: #f1f5f9 !important;
}

.total-cell-wrap {
  position: relative;
  display: inline-block;
  cursor: help;
}

.total-cell-value {
  font-weight: 600;
  color: #0d4169;
  border-bottom: 1px dotted #94a3b8;
}

.total-cell-value.vat-total-value {
  font-weight: 700;
  color: #334155;
}

.today-cash-table td.variation-cell {
  color: #64748b;
  font-variant-numeric: tabular-nums;
}

.total-cell-dropdown {
  display: none;
  position: absolute;
  top: calc(100% + 4px);
  left: 50%;
  transform: translateX(-50%);
  z-index: 60;
  min-width: 13rem;
  padding: 8px 10px;
  background: #fff;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.12);
  text-align: left;
  white-space: nowrap;
}

.total-cell-wrap:hover .total-cell-dropdown {
  display: block;
}

.pinned-breakdown-wrap:hover .total-cell-dropdown {
  display: block;
}

.saved-income-report-scroll {
  padding-bottom: 1rem;
}

.total-cell-dropdown-title {
  font-size: 0.62rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #64748b;
  margin-bottom: 6px;
  padding-bottom: 4px;
  border-bottom: 1px solid #e2e8f0;
}

.total-cell-dropdown-line {
  font-size: 0.68rem;
  color: #334155;
  line-height: 1.5;
}

.total-cell-dropdown-line + .total-cell-dropdown-line {
  margin-top: 4px;
}

.total-cell-tag {
  display: inline-block;
  min-width: 3.25rem;
  margin-right: 4px;
  font-size: 0.62rem;
  font-weight: 700;
  text-transform: uppercase;
  color: #0d4169;
}

</style>
